import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Bot,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Clock3,
  Download,
  ExternalLink,
  FileText,
  Flag,
  Gavel,
  Landmark,
  LineChart,
  LockKeyhole,
  MessageSquare,
  Radio,
  Scale,
  Search,
  Share2,
  ShieldCheck,
  Sparkles,
  Trash2,
  UsersRound,
  Vote
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { indiaMapRegions, indiaMapViewBox } from "./indiaMap";

type ApiState = {
  app: {
    domain: string;
    disclaimer: string;
    featureFlags: Record<string, boolean>;
  };
  user: null | {
    id: string;
    displayName: string;
    homeSwarmSlug: string | null;
    ageBracket: string;
    role: string;
    onboardingComplete: boolean;
    swarmLockedUntil: string | null;
    anonymousMode: boolean;
  };
  geo?: {
    source: string;
    city?: string;
    region?: string;
    country?: string;
    label: string;
    searchQuery: string;
    matched: boolean;
    confidence: string;
    suggestedSwarm: Swarm | null;
  };
  counts: {
    constituencies: number;
    expectedConstituencies: number;
    representatives: number;
    vacancies: number;
    issues: number;
    users?: number;
    activeUsers?: number;
    votes?: number;
    comments?: number;
    communityDiscussions?: number;
    communityPolls?: number;
  };
  data: {
    generatedAt: string;
    sources: Record<string, string>;
    quality: {
      actualConstituencies: number;
      currentRepresentatives: number;
      vacancies: number;
      vacantConstituencies?: string[];
    };
  };
};

type StanceKey = "support" | "needs_reform" | "oppose";
type IssueDocument = string | { title: string; source?: string; url?: string; note?: string };

type Issue = {
  id: string;
  slug: string;
  title: string;
  category: string;
  riskLevel: string;
  status: string;
  brief: string;
  whyItMatters: string;
  documents: IssueDocument[];
  poll: {
    id: string;
    question: string;
    status: string;
    options: Array<{ id: StanceKey; label: string }>;
  };
  aiSummary: {
    label: string;
    positions: Record<string, { topArgument: string; commonConcern: string; evidenceCited?: string; counterpoint: string; reviewFlags: string[] }>;
  };
};

type BootstrapPayload = ApiState & { issues?: Issue[] };

declare global {
  interface Window {
    __RS_BOOTSTRAP__?: BootstrapPayload;
  }
}

type Swarm = {
  slug: string;
  name: string;
  state: string;
  lsSeatCode: string;
  representative?: Representative;
  signupCount?: number;
};

type Representative = {
  id: string;
  constituencySlug: string;
  name: string;
  party: string;
  partyShort: string;
  status: string;
  photoUrl?: string;
  photoSource?: string;
  contact?: { email?: string[] };
};

type Results = {
  nationalResult: Record<string, number>;
  swarmResult: Record<string, number>;
  totalVotes: number;
  swarmVotes?: number;
  constituenciesRepresented: number;
  updatedAt: string;
  advisoryDisclaimer: string;
};

type UserPollVote = {
  optionId: StanceKey;
  swarmId: string;
  votedAt: string;
};

type CommentRecord = {
  id: string;
  issueId: string;
  userLabel: string;
  position: StanceKey;
  body: string;
  status: string;
  createdAt: string;
  upvotes?: number;
};

type ModerationStatus = "pending" | "published" | "blocked";

type CommunityPollOption = {
  id: string;
  label: string;
  voteCount: number;
  percent: number;
};

type CommunityPollRecord = {
  id: string;
  discussionId: string | null;
  discussionSlug: string | null;
  discussionTitle: string | null;
  question: string;
  status: ModerationStatus;
  createdAt: string;
  userLabel: string;
  options: CommunityPollOption[];
  totalVotes: number;
  userVoteOptionId: string | null;
};

type DiscussionCommentRecord = {
  id: string;
  discussionId: string;
  userLabel: string;
  body: string;
  status: "published" | "needs_review" | "blocked";
  createdAt: string;
};

type CommunityDiscussionRecord = {
  id: string;
  slug: string;
  title: string;
  body: string;
  status: ModerationStatus;
  createdAt: string;
  userLabel: string;
  commentCount: number;
  poll: CommunityPollRecord | null;
  comments?: DiscussionCommentRecord[];
};

type TransparencyReport = {
  signups: number;
  activeUsers: number;
  votesCast: number;
  constituenciesRepresented: number;
  shareCardsGenerated: number;
  commentsPosted: number;
  pollsOpened: number;
  auditRoot?: string;
};

type HeatMapRow = {
  swarm: Swarm;
  result: Results;
};

type LiveMapLeader = StanceKey | "none";

type LiveMapStateSignal = {
  state: string;
  voteCount: number;
  support: number;
  needs_reform: number;
  oppose: number;
  leader: LiveMapLeader;
};

type LiveMapConstituencySignal = {
  slug: string;
  name: string;
  state: string;
  voteCount: number;
  support: number;
  needs_reform: number;
  oppose: number;
  leader: LiveMapLeader;
};

type LiveMapPayload = {
  pollId: string;
  nationalResult: Record<StanceKey, number>;
  totalVotes: number;
  constituenciesRepresented: number;
  updatedAt: string;
  states: LiveMapStateSignal[];
  topConstituencies: LiveMapConstituencySignal[];
};

const stanceLabels = {
  support: "Support",
  needs_reform: "Needs Reform",
  oppose: "Oppose"
} satisfies Record<StanceKey, string>;

function issueDocumentMeta(document: IssueDocument) {
  return typeof document === "string" ? { title: document, source: "", url: "" } : document;
}

const pendingSwarmStorageKey = "rs_pending_swarm_slug";
const pendingReturnToStorageKey = "rs_pending_return_to";
const navigationFeedbackEvent = "rs:navigation-feedback";
let navigationFeedbackTimer: number | null = null;

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {})
    }
  });
  const payload = (await response.json()) as { message?: string; code?: string };
  if (!response.ok) {
    throw new Error(payload.message || payload.code || "Request failed");
  }
  return payload as T;
}

function navigate(path: string) {
  if (navigationFeedbackTimer !== null) window.clearTimeout(navigationFeedbackTimer);
  window.dispatchEvent(new CustomEvent(navigationFeedbackEvent, { detail: { path, active: true } }));
  history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    navigationFeedbackTimer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent(navigationFeedbackEvent, { detail: { path, active: false } }));
      navigationFeedbackTimer = null;
    }, 280);
  });
}

function safeReturnPath(value?: string | null) {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/api/")) return null;
  return value.slice(0, 300);
}

function storageGet(key: string) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Local storage can be unavailable in strict privacy modes.
  }
}

function storageRemove(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Local storage can be unavailable in strict privacy modes.
  }
}

function rememberJoinIntent(swarmSlug?: string | null, returnTo?: string | null) {
  if (swarmSlug) storageSet(pendingSwarmStorageKey, swarmSlug);
  const safeReturnTo = safeReturnPath(returnTo);
  if (safeReturnTo) storageSet(pendingReturnToStorageKey, safeReturnTo);
}

function clearJoinIntent() {
  storageRemove(pendingSwarmStorageKey);
  storageRemove(pendingReturnToStorageKey);
}

function currentAppPath() {
  return `${window.location.pathname}${window.location.search}`;
}

function buildJoinPath({ swarmSlug, returnTo }: { swarmSlug?: string | null; returnTo?: string | null } = {}) {
  const params = new URLSearchParams();
  if (swarmSlug) params.set("swarm", swarmSlug);
  const safeReturnTo = safeReturnPath(returnTo);
  if (safeReturnTo) params.set("returnTo", safeReturnTo);
  const query = params.toString();
  return query ? `/join?${query}` : "/join";
}

function startJoin(swarmSlug?: string | null, returnTo = "/sabha") {
  rememberJoinIntent(swarmSlug, returnTo);
  navigate(buildJoinPath({ swarmSlug, returnTo }));
}

function openOnKey(event: KeyboardEvent<HTMLElement>, path: string) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  navigate(path);
}

function navigateAfterPaint(path: string) {
  requestAnimationFrame(() => navigate(path));
}

function compactNumber(value: number) {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 100_000) return `${Math.round(value / 1000)}K`;
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10_000 ? 0 : 1)}K`;
  return value.toLocaleString();
}

function absoluteAppUrl(path = currentAppPath()) {
  return new URL(path, window.location.origin).toString();
}

async function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement("textarea");
  input.value = value;
  input.setAttribute("readonly", "");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  document.body.removeChild(input);
}

function websocketUrl(path: string) {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}${path}`;
}

function leaderLabel(leader: LiveMapLeader) {
  return leader === "none" ? "Awaiting votes" : stanceLabels[leader];
}

function heatLevelFor(voteCount: number, maxVotes: number) {
  if (voteCount <= 0 || maxVotes <= 0) return 0;
  const ratio = voteCount / maxVotes;
  if (ratio <= 0.18) return 1;
  if (ratio <= 0.38) return 2;
  if (ratio <= 0.62) return 3;
  if (ratio <= 0.84) return 4;
  return 5;
}

function heatLabel(level: number) {
  if (level === 0) return "No votes";
  if (level === 1) return "Low";
  if (level === 2) return "Rising";
  if (level === 3) return "Warm";
  if (level === 4) return "High";
  return "Hottest";
}

const popularSwarmSearches = ["New Delhi", "Bangalore Central", "Mumbai North", "Kolkata South"];

function InlineLoader({ label, className = "" }: { label: string; className?: string }) {
  return (
    <span className={`inline-loader ${className}`.trim()}>
      <span className="inline-spinner" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

function BusyContent({ busy, busyLabel, icon, children }: { busy: boolean; busyLabel: string; icon: ReactNode; children: ReactNode }) {
  if (busy) return <InlineLoader label={busyLabel} />;
  return (
    <>
      {icon}
      {children}
    </>
  );
}

export function App() {
  const initialBootstrap = window.__RS_BOOTSTRAP__;
  const [path, setPath] = useState(window.location.pathname);
  const [bootstrap, setBootstrap] = useState<ApiState | null>(() => initialBootstrap ?? null);
  const [issues, setIssues] = useState<Issue[]>(() => initialBootstrap?.issues ?? []);
  const [error, setError] = useState<string | null>(null);
  const [navigatingTo, setNavigatingTo] = useState<string | null>(null);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const onNavigationFeedback = (event: Event) => {
      const detail = (event as CustomEvent<{ path?: string; active?: boolean }>).detail;
      setNavigatingTo(detail?.active ? detail.path ?? currentAppPath() : null);
    };
    window.addEventListener(navigationFeedbackEvent, onNavigationFeedback);
    return () => window.removeEventListener(navigationFeedbackEvent, onNavigationFeedback);
  }, []);

  useEffect(() => {
    if (bootstrap && issues.length) return;
    api<BootstrapPayload>("/api/bootstrap")
      .then((boot) => {
        setBootstrap(boot);
        setIssues(boot.issues ?? []);
      })
      .catch((err: Error) => setError(err.message));
  }, []);

  const page = useMemo(() => {
    if (path === "/") return <Home bootstrap={bootstrap} issues={issues} />;
    if (path === "/join") return <Join initialBootstrap={bootstrap} />;
    if (path === "/account") return <AccountPage bootstrap={bootstrap} onUserUpdate={(user) => setBootstrap((current) => (current ? { ...current, user } : current))} />;
    if (path === "/sabha" || path === "/issues" || path === "/issues/") return <Sabha issues={issues} bootstrap={bootstrap} />;
    if (path === "/polls") return <PollsPage issues={issues} bootstrap={bootstrap} />;
    if (path === "/pulse") return <PulsePage bootstrap={bootstrap} issues={issues} />;
    if (path === "/community") return <Community issues={issues} bootstrap={bootstrap} />;
    if (path === "/swarms") return <SwarmsIndex />;
    if (path.startsWith("/discussions/")) return <DiscussionPage slug={decodeURIComponent(path.replace("/discussions/", ""))} bootstrap={bootstrap} />;
    if (path.startsWith("/issues/")) return <IssuePage path={path} issues={issues} bootstrap={bootstrap} />;
    if (path.startsWith("/swarms/")) return <SwarmPage slug={decodeURIComponent(path.replace("/swarms/", ""))} />;
    if (path.startsWith("/representatives/")) return <RepresentativePage id={decodeURIComponent(path.replace("/representatives/", ""))} />;
    if (path.startsWith("/mandates/")) return <MandatePage slug={decodeURIComponent(path.replace("/mandates/", ""))} />;
    return <NotFound />;
  }, [bootstrap, issues, path]);

  return (
    <>
      <div className="top-announcement">
        <button onClick={() => navigate("/pulse")} title="Open civic pulse">
          <span>NEW</span>
          NEET vote is live. Put your constituency on the map.
          <ArrowRight size={14} />
        </button>
      </div>
      {navigatingTo && (
        <div className="route-loader" role="status" aria-live="polite">
          <InlineLoader label={navigatingTo.startsWith("/issues/") ? "Opening vote" : "Opening"} />
        </div>
      )}
      <header className="site-header">
        <a className="brand" href="/" onClick={(event) => preventNav(event, "/")} aria-label="Roachsabha home">
          <span className="brand-mark">
            <img src="/roach-mark.svg" alt="" aria-hidden="true" />
          </span>
          <span className="brand-text">
            <strong>ROACHSABHA</strong>
            <small>A Cockroach Assembly.</small>
          </span>
        </a>
        <nav aria-label="Primary">
          {[
            ["/swarms", "Constituencies"],
            ["/sabha", "Votes"],
            ["/polls", "Polls"],
            ["/community", "Community"]
          ].map(([href, label]) => (
            <a key={href} className={isActivePath(path, href) ? "active" : ""} aria-current={isActivePath(path, href) ? "page" : undefined} href={href} onClick={(event) => preventNav(event, href)}>
              {label}
            </a>
          ))}
        </nav>
        <div className="header-actions">
          <button className="login-chip" onClick={() => (bootstrap?.user ? navigate("/account") : startJoin(undefined, currentAppPath()))} title={bootstrap?.user ? "Manage account" : "Log in"}>
            {bootstrap?.user ? "Account" : "Log in"}
          </button>
          <button
            className="account-chip"
            onClick={() => (bootstrap?.user?.homeSwarmSlug ? navigate(`/swarms/${bootstrap.user.homeSwarmSlug}`) : startJoin(undefined, currentAppPath()))}
            title={bootstrap?.user?.homeSwarmSlug ? "Open your constituency" : bootstrap?.user ? "Finish setup" : "Join Roachsabha"}
          >
            <BadgeCheck size={16} />
            {bootstrap?.user?.homeSwarmSlug ? "My Seat" : bootstrap?.user ? "Finish setup" : "Join Roachsabha"}
          </button>
        </div>
      </header>
      {!bootstrap && !error && <div className="global-loader" role="status"><InlineLoader label="Loading civic data" /></div>}
      {error ? (
        <main className="page narrow">
          <Status tone="danger" icon={<AlertTriangle />} title="Application error" body={error} />
        </main>
      ) : (
        page
      )}
      <footer className="site-footer">
        <div className="footer-claim">
          <ShieldCheck size={28} />
          <strong>Public pressure. Private ballots. Non-binding signals.</strong>
        </div>
        <p>{bootstrap?.app.disclaimer ?? "Roachsabha publishes advisory citizen signals. It is not an official poll, election, law, court, or government platform."}</p>
        <div className="footer-links" aria-label="Platform principles">
          <span><Scale size={15} /> Transparent counts</span>
          <span><LockKeyhole size={15} /> Private individual votes</span>
          <span><UsersRound size={15} /> Constituency-first action</span>
        </div>
      </footer>
    </>
  );
}

function isActivePath(current: string, href: string) {
  if (href === "/sabha" && (current === "/issues" || current.startsWith("/issues/"))) return true;
  return current === href || (href !== "/" && current.startsWith(`${href}/`));
}

function preventNav(event: MouseEvent<HTMLAnchorElement>, path: string) {
  event.preventDefault();
  navigate(path);
}

function Home({ bootstrap, issues }: { bootstrap: ApiState | null; issues: Issue[] }) {
  const [results, setResults] = useState<Swarm[]>([]);
  const [featuredResults, setFeaturedResults] = useState<Record<string, Results>>({});
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const geo = bootstrap?.geo;
  const usingIpSuggestion = Boolean(geo?.matched && query === geo.searchQuery);
  const triedIpLocation = Boolean(geo && !geo.matched && geo.searchQuery && query === geo.searchQuery);

  async function search(value: string) {
    setQuery(value);
    setSearchError(null);
    if (!value.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const payload = await api<{ results: Swarm[] }>(`/api/swarms/search?q=${encodeURIComponent(value)}`);
      setResults(payload.results);
    } catch (err) {
      setSearchError((err as Error).message);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  useEffect(() => {
    if (!bootstrap) return;
    if (bootstrap.geo?.matched && bootstrap.geo.searchQuery) {
      const timer = window.setTimeout(() => search(bootstrap.geo?.searchQuery ?? "").catch(() => undefined), 250);
      return () => window.clearTimeout(timer);
    }
  }, [bootstrap?.geo?.searchQuery]);

  const featuredIssues = issues.slice(0, 4);
  const campaignIssue = issues.find((issue) => issue.slug === "neet-reform") ?? featuredIssues[0];
  const campaignPath = campaignIssue ? `/issues/${campaignIssue.slug}` : "/sabha";
  const featuredIssueKey = featuredIssues.map((issue) => issue.slug).join("|");
  const focusSwarmSearch = () => {
    document.getElementById("home-swarm-search")?.scrollIntoView({ block: "center", behavior: "smooth" });
    window.setTimeout(() => document.getElementById("home-swarm-search")?.focus(), 120);
  };

  useEffect(() => {
    if (!featuredIssues.length) return;
    let active = true;
    Promise.all(
      featuredIssues.map(async (issue) => {
        const payload = await api<Results>(`/api/issues/${issue.slug}/results`);
        return [issue.slug, payload] as const;
      })
    )
      .then((entries) => {
        if (active) setFeaturedResults(Object.fromEntries(entries));
      })
      .catch(() => {
        if (active) setFeaturedResults({});
      });
    return () => {
      active = false;
    };
  }, [featuredIssueKey]);

  return (
    <main>
      <section className="home-hero">
        <div className="hero-copy">
          <p className="eyebrow">Live civic vote / 543 constituencies</p>
          <h1>Vote on NEET Reform from your constituency.</h1>
          <p className="lede">Find your seat, cast one private vote, and see the public signal for your constituency.</p>
          <div className="hero-actions">
            <button className="primary" onClick={() => navigate(campaignPath)} title="Vote on NEET Reform">
              <Vote size={18} /> Vote in 30 seconds
            </button>
            <button className="secondary" onClick={focusSwarmSearch} title="Find your constituency">
              <Search size={18} /> Find my constituency
            </button>
          </div>
        </div>
        <div className="hero-action-panel">
          <div className="hero-mascot" aria-label="Roachsabha identity">
            <img src="/roach-mark.svg" alt="" aria-hidden="true" />
            <strong>YES, WE ARE unemployed Cockroaches</strong>
          </div>
        </div>
      </section>

      <section className="page home-start-section" aria-label="Start here">
        <div className="home-start-grid">
          <div className="home-start-copy">
            <div className="campaign-question">
              <span>Today's vote</span>
              <strong>{campaignIssue?.poll.question ?? "Should NEET be reformed before the next admission cycle?"}</strong>
              <button className="primary" onClick={() => navigate(campaignPath)}>Open vote <ArrowRight size={16} /></button>
            </div>
            <div className="home-signal-stack" aria-label="Platform snapshot">
              <div className="hero-metrics home-start-metrics">
                <span><strong>{bootstrap?.counts.constituencies ?? 543}</strong> constituencies</span>
                <span><strong>{campaignIssue ? "1" : issues.length || bootstrap?.counts.issues || 10}</strong> live vote</span>
                <span><strong>{bootstrap?.counts.votes ?? 0}</strong> votes recorded</span>
              </div>
              <p className="home-advisory"><Scale size={17} /> Roachsabha is public pressure, not official election data. Individual votes stay private.</p>
            </div>
          </div>
          <div className="swarm-finder find-swarm-card" aria-label="Find your constituency">
            <div className="panel-head">
              <div>
                <p className="eyebrow">Find your voting seat</p>
                <label htmlFor="home-swarm-search">{usingIpSuggestion ? "Approximate match found" : "Search your constituency"}</label>
              </div>
              <Search size={18} />
            </div>
            {geo && (geo.matched || geo.searchQuery) && (
              <p className={`panel-note ${geo.matched ? "success" : ""}`}>
                {geo.matched
                  ? `Approximate location suggests ${geo.label}. Confirm before you join or vote.`
                  : geo.searchQuery
                    ? `Approximate location detected as ${geo.label}. Search manually for your Lok Sabha seat.`
                    : "Approximate location is unavailable. Search manually."}
              </p>
            )}
            <div className="search-row" aria-busy={searching}>
              <Search size={18} />
              <input
                id="home-swarm-search"
                value={query}
                onChange={(event) => search(event.target.value)}
                placeholder="Try Bangalore, Delhi, Pune"
              />
            </div>
            <div className="swarm-results">
              {searching && <p className="panel-note">Searching constituencies...</p>}
              {searchError && <p className="panel-note danger">{searchError}</p>}
              {!searching && !searchError && query && results.length === 0 && (
                <p className="panel-note">{triedIpLocation ? "No seat matched the approximate location. Try a city, state, or constituency name in India." : "No seat matched. Try a city, state, or constituency name."}</p>
              )}
              {!query && (
                <div className="popular-searches">
                  <span>Popular:</span>
                  {popularSwarmSearches.map((item) => (
                    <button key={item} type="button" onClick={() => search(item)}>{item}</button>
                  ))}
                </div>
              )}
              {results.slice(0, 5).map((swarm) => (
                <article key={swarm.slug} className="result-choice">
                  <div className="result-choice-main">
                    <RepresentativePhoto rep={swarm.representative} size="small" />
                    <span className="result-main">
                      <strong>{swarm.name}</strong>
                      <small>{swarm.representative ? `${swarm.representative.name} / ${swarm.representative.partyShort}` : swarm.state}{typeof swarm.signupCount === "number" ? ` / ${compactNumber(swarm.signupCount)} signed up` : ""}</small>
                    </span>
                  </div>
                  <div className="result-actions">
                    <button className="primary" onClick={() => startJoin(swarm.slug, "/sabha")} title={`Join ${swarm.name}`}>
                      <BadgeCheck size={16} /> Join this seat
                    </button>
                    <button className="secondary" onClick={() => navigate(`/swarms/${swarm.slug}`)} title={`View ${swarm.name}`}>
                      <ArrowRight size={16} /> See signal
                    </button>
                  </div>
                </article>
              ))}
            </div>
            <p className="finder-footnote"><BadgeCheck size={16} /> Your seat unlocks local results, share cards, and issue votes.</p>
          </div>
        </div>
      </section>

      <LiveIndiaVoteMap issues={issues} defaultIssueSlug={campaignIssue?.slug} />

      <section className="page home-section">
        <div className="workspace-section-head">
          <div>
            <p className="eyebrow">Votes worth spreading</p>
            <h2>Pick the issue your group chat should settle next</h2>
          </div>
          <button className="secondary" onClick={() => navigate("/sabha")}>View all votes <ArrowRight size={16} /></button>
        </div>
        <div className="featured-bill-grid">
          {featuredIssues.map((issue, index) => (
            <FeaturedBillCard key={issue.slug} issue={issue} result={featuredResults[issue.slug]} />
          ))}
          {issues.length === 0 && <Status tone="info" icon={<Sparkles />} title="Issues loading" body="Live issue briefs are being fetched." />}
        </div>
      </section>

      <section className="page home-split">
        <TopSwarmsPanel />
        <HowItWorksPanel />
      </section>
    </main>
  );
}

function LiveIndiaVoteMap({ issues, defaultIssueSlug }: { issues: Issue[]; defaultIssueSlug?: string }) {
  const liveIssues = useMemo(() => {
    const eligibleIssues = issues.filter((item) => item.poll?.id);
    if (!defaultIssueSlug) return eligibleIssues;
    const defaultIssue = eligibleIssues.find((item) => item.slug === defaultIssueSlug);
    if (!defaultIssue) return eligibleIssues;
    return [defaultIssue, ...eligibleIssues.filter((item) => item.slug !== defaultIssueSlug)];
  }, [defaultIssueSlug, issues]);
  const liveIssueKey = liveIssues.map((item) => item.slug).join("|");
  const [activeIssueSlug, setActiveIssueSlug] = useState(defaultIssueSlug ?? "");
  const [liveMap, setLiveMap] = useState<LiveMapPayload | null>(null);
  const [status, setStatus] = useState("Loading");
  const [liveState, setLiveState] = useState<"connecting" | "live" | "paused">("connecting");

  useEffect(() => {
    if (!liveIssues.length) return;
    setActiveIssueSlug((current) => (liveIssues.some((item) => item.slug === current) ? current : liveIssues[0].slug));
  }, [liveIssueKey]);

  const issue = liveIssues.find((item) => item.slug === activeIssueSlug) ?? liveIssues[0];
  const activeIssueIndex = issue ? liveIssues.findIndex((item) => item.slug === issue.slug) : -1;
  const canChangeIssue = liveIssues.length > 1;

  const moveIssue = (direction: -1 | 1) => {
    if (!canChangeIssue || activeIssueIndex < 0) return;
    const nextIndex = (activeIssueIndex + direction + liveIssues.length) % liveIssues.length;
    setActiveIssueSlug(liveIssues[nextIndex].slug);
  };

  useEffect(() => {
    if (!issue) return;
    let active = true;
    let socket: WebSocket | null = null;
    setLiveMap(null);
    setLiveState("connecting");

    const loadSnapshot = async () => {
      setStatus("Loading");
      try {
        const payload = await api<{ liveMap: LiveMapPayload }>(`/api/issues/${issue.slug}/live-map`);
        if (active) {
          setLiveMap(payload.liveMap);
          setStatus(payload.liveMap.totalVotes ? "Live" : "Waiting for the first vote");
        }
      } catch (err) {
        if (active) setStatus((err as Error).message);
      }
    };

    loadSnapshot().catch(() => undefined);

    try {
      socket = new WebSocket(websocketUrl(`/api/polls/${encodeURIComponent(issue.poll.id)}/live`));
      socket.onopen = () => {
        if (!active) return;
        setLiveState("live");
        setStatus("Live");
      };
      socket.onmessage = (event) => {
        let message: { type?: string; pollId?: string; liveMap?: LiveMapPayload };
        try {
          message = JSON.parse(String(event.data)) as { type?: string; pollId?: string; liveMap?: LiveMapPayload };
        } catch {
          return;
        }
        if (!active || message.type !== "poll_update" || message.pollId !== issue.poll.id || !message.liveMap) return;
        setLiveMap(message.liveMap);
        setStatus("Live");
      };
      socket.onerror = () => {
        if (active) setLiveState("paused");
      };
      socket.onclose = () => {
        if (active) setLiveState("paused");
      };
    } catch {
      setLiveState("paused");
    }

    return () => {
      active = false;
      socket?.close();
    };
  }, [issue?.poll.id, issue?.slug]);

  if (!issue) return null;

  const stateSignals = new Map((liveMap?.states ?? []).map((item) => [item.state, item]));
  const maxStateVotes = Math.max(0, ...(liveMap?.states ?? []).map((item) => item.voteCount));
  const support = liveMap?.nationalResult.support ?? 0;
  const updatedAt = liveMap?.updatedAt ? new Date(liveMap.updatedAt).toLocaleTimeString() : "Pending";
  const topConstituencies = liveMap?.topConstituencies ?? [];

  return (
    <section className="page home-live-map-section" aria-label="Live India vote map">
      <div className="workspace-section-head">
        <div>
          <p className="eyebrow">Live</p>
          <h2>{issue.title}</h2>
          <p>{issue.poll.question}</p>
        </div>
        <div className="live-map-actions" aria-label="Change live map issue">
          <button className="icon-button" type="button" onClick={() => moveIssue(-1)} disabled={!canChangeIssue} aria-label="Previous issue">
            <ChevronLeft size={18} />
          </button>
          <span className={`live-pill ${liveState}`}>
            <Radio size={15} />
            Live
          </span>
          <button className="icon-button" type="button" onClick={() => moveIssue(1)} disabled={!canChangeIssue} aria-label="Next issue">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
      <div className="live-issue-carousel" aria-label="Live issue carousel">
        {liveIssues.map((item, index) => (
          <button key={item.slug} className={item.slug === issue.slug ? "active" : ""} type="button" onClick={() => setActiveIssueSlug(item.slug)} aria-label={`Show ${item.title}`}>
            <span>{index + 1}</span>
            <strong>{item.title}</strong>
          </button>
        ))}
      </div>
      <div className="live-map-layout">
        <div className="india-map-panel">
          <svg className="india-live-map" viewBox={indiaMapViewBox} role="img" aria-label={`India map for ${issue.title} vote totals`}>
            <g className="india-region-layer">
              {indiaMapRegions.map((region) => {
                const signal = stateSignals.get(region.state);
                const voteCount = signal?.voteCount ?? 0;
                const leader = signal?.leader ?? "none";
                const heatLevel = heatLevelFor(voteCount, maxStateVotes);
                return (
                  <path key={region.state} className={`india-region heat-${heatLevel} ${voteCount ? "active" : ""}`} d={region.d} fillRule="evenodd">
                    <title>{`${region.state}: ${compactNumber(voteCount)} votes / ${heatLabel(heatLevel)} / ${leaderLabel(leader)}`}</title>
                  </path>
                );
              })}
            </g>
            <g className="india-label-layer" aria-hidden="true">
              {indiaMapRegions.map((region) => {
                const signal = stateSignals.get(region.state);
                const voteCount = signal?.voteCount ?? 0;
                return <text key={region.state} className={`india-map-label ${voteCount ? "active" : ""}`} x={region.labelX} y={region.labelY}>{region.label}</text>;
              })}
            </g>
          </svg>
          <div className="map-legend" aria-label="Map legend">
            <span><i className="heat-0" /> 0</span>
            <span><i className="heat-1" /> Low</span>
            <span><i className="heat-2" /> Rising</span>
            <span><i className="heat-3" /> Warm</span>
            <span><i className="heat-4" /> High</span>
            <span><i className="heat-5" /> Hottest</span>
          </div>
        </div>
        <aside className="live-map-rail">
          <div className="live-map-metrics">
            <span><strong>{compactNumber(liveMap?.totalVotes ?? 0)}</strong><small>Votes</small></span>
            <span><strong>{liveMap?.constituenciesRepresented ?? 0}</strong><small>Seats lit</small></span>
            <span><strong>{support}%</strong><small>Support</small></span>
          </div>
          <p className="panel-note success"><CircleDot size={15} /> {status} / updated {updatedAt}</p>
          <div className="live-constituency-list" aria-label="Top live constituencies">
            {topConstituencies.map((item) => (
              <button key={item.slug} onClick={() => navigate(`/swarms/${item.slug}`)}>
                <span>
                  <strong>{item.name}</strong>
                  <small>{item.state} / {compactNumber(item.voteCount)} votes</small>
                </span>
                <b>{leaderLabel(item.leader)}</b>
              </button>
            ))}
            {topConstituencies.length === 0 && <p className="panel-note">No constituency has voted on this issue yet. The first vote will light up the map.</p>}
          </div>
          <button className="primary full" onClick={() => navigate(`/issues/${issue.slug}`)}>
            <Vote size={18} /> Vote and light up your seat
          </button>
        </aside>
      </div>
    </section>
  );
}

function StatsBand({ bootstrap }: { bootstrap: ApiState | null }) {
  const stats = [
    ["Constituencies", bootstrap?.counts.constituencies ?? 543],
    ["Current representatives", bootstrap?.counts.representatives ?? 0],
    ["Votes recorded", bootstrap?.counts.votes ?? 0],
    ["Issues", bootstrap?.counts.issues ?? 10]
  ];
  return (
    <section className="stats-band" aria-label="Platform status">
      {stats.map(([label, value]) => (
        <div key={label}>
          <strong>{value}</strong>
          <span>{label}</span>
        </div>
      ))}
    </section>
  );
}

function FeaturedBillCard({ issue, result }: { issue: Issue; result?: Results }) {
  const path = `/issues/${issue.slug}`;
  const support = result ? result.nationalResult.support ?? 0 : null;
  const votes = result ? result.totalVotes : null;
  return (
    <article className="featured-bill-card" role="link" tabIndex={0} onClick={() => navigate(path)} onKeyDown={(event) => openOnKey(event, path)} aria-label={`Open ${issue.title}`}>
      <div className="bill-card-top">
        <span className="bill-icon"><FileText size={20} /></span>
        <span className="mini-chip active">Active</span>
      </div>
      <h3>{issue.title}</h3>
      <p>{issue.brief}</p>
      <div className="bill-stats">
        <span><small>National support</small><strong>{support === null ? "Loading" : `${support}%`}</strong></span>
        <span><small>Votes</small><strong>{votes === null ? "Loading" : compactNumber(votes)}</strong></span>
      </div>
      <span className="text-arrow">Open the vote <ArrowRight size={15} /></span>
    </article>
  );
}

function TopSwarmsPanel() {
  const swarms = [
    { name: "New Delhi", state: "NCT of Delhi", slug: "nct-of-delhi-new-delhi" },
    { name: "Bangalore Central", state: "Karnataka", slug: "karnataka-bangalore-central" },
    { name: "Mumbai North", state: "Maharashtra", slug: "maharashtra-mumbai-north" },
    { name: "Pune", state: "Maharashtra", slug: "maharashtra-pune" },
    { name: "Bangalore South", state: "Karnataka", slug: "karnataka-bangalore-south" }
  ];
  return (
    <section className="home-panel">
      <p className="eyebrow">Signals to watch</p>
      <div className="swarm-table">
        {swarms.map(({ name, state, slug }, index) => (
          <button key={name} onClick={() => navigate(`/swarms/${slug}`)}>
            <strong>{index + 1}</strong>
            <span><b>{name}</b><small>{state}</small></span>
            <span className="status-text">Open map</span>
            <ArrowRight size={15} />
          </button>
        ))}
      </div>
    </section>
  );
}

function HowItWorksPanel() {
  const steps = [
    ["Join your seat", "Choose your Lok Sabha constituency once so every signal has a local home."],
    ["Vote fast", "Read the plain brief, pick a stance, and add a public argument if you want."],
    ["Share the signal", "Show friends where your constituency stands and invite them to add weight."],
    ["Build mandates", "When enough citizens align, the signal becomes a reviewed, non-binding mandate."]
  ];
  return (
    <section className="home-panel">
      <p className="eyebrow">The action loop</p>
      <div className="work-steps">
        {steps.map(([title, body], index) => (
          <div key={title}>
            <span>{index + 1}</span>
            <FileText size={22} />
            <p><strong>{title}</strong><small>{body}</small></p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SwarmsIndex() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Swarm[]>([]);
  const [status, setStatus] = useState("");

  async function search(value: string) {
    setQuery(value);
    if (!value.trim()) {
      setResults([]);
      setStatus("");
      return;
    }
    setStatus("Loading constituency signals...");
    try {
      const payload = await api<{ results: Swarm[] }>(`/api/swarms/search?q=${encodeURIComponent(value)}`);
      setResults(payload.results);
      setStatus(payload.results.length ? "" : "No constituency matched this search.");
    } catch (err) {
      setResults([]);
      setStatus((err as Error).message);
    }
  }

  return (
    <main className="page swarms-index">
      <SectionTitle
        icon={<UsersRound />}
        title="Find the Constituency Signal"
        action="543 constituencies"
        body="Search your Lok Sabha seat, see its public pulse, and invite people from the same seat to make the signal harder to ignore."
      />
      <section className="tool-panel">
        <label htmlFor="swarms-index-search">Search constituency, city, or state</label>
        <div className="search-row">
          <Search size={18} />
          <input id="swarms-index-search" value={query} onChange={(event) => search(event.target.value)} placeholder="Try Bangalore, Delhi, Pune" />
        </div>
        {!query && (
          <div className="popular-searches">
            <span>Popular:</span>
            {popularSwarmSearches.map((item) => (
              <button key={item} type="button" onClick={() => search(item)}>{item}</button>
            ))}
          </div>
        )}
      </section>
      {status && <Status tone={results.length ? "info" : "warning"} icon={<Search />} title="Constituency search" body={status} />}
      <section className="swarm-directory" aria-label="Constituency results">
        {results.map((swarm) => (
          <article key={swarm.slug} className="directory-card">
            <RepresentativePhoto rep={swarm.representative} size="small" />
            <div>
              <h2>{swarm.name}</h2>
              <p>{swarm.state} / {swarm.representative ? `${swarm.representative.name} (${swarm.representative.partyShort})` : "Representative record unavailable"}{typeof swarm.signupCount === "number" ? ` / ${compactNumber(swarm.signupCount)} signed up` : ""}</p>
            </div>
            <div className="directory-actions">
              <button className="secondary" onClick={() => navigate(`/swarms/${swarm.slug}`)}><LineChart size={16} /> Open signal</button>
              <button className="primary" onClick={() => startJoin(swarm.slug, `/swarms/${swarm.slug}`)}><BadgeCheck size={16} /> Join this seat</button>
            </div>
          </article>
        ))}
      </section>
    </main>
  );
}

function PollsPage({ issues, bootstrap }: { issues: Issue[]; bootstrap: ApiState | null }) {
  const signedIn = Boolean(bootstrap?.user);
  const [communityPolls, setCommunityPolls] = useState<CommunityPollRecord[]>([]);
  const [discussions, setDiscussions] = useState<CommunityDiscussionRecord[]>([]);
  const [communityLoading, setCommunityLoading] = useState(true);
  const [communityStatus, setCommunityStatus] = useState<string | null>(null);
  const [voting, setVoting] = useState<string | null>(null);
  const [openingSlug, setOpeningSlug] = useState<string | null>(null);
  const openPoll = (slug: string) => {
    setOpeningSlug(slug);
    navigateAfterPaint(`/issues/${slug}`);
  };

  async function refreshCommunityPolls() {
    setCommunityLoading(true);
    try {
      const [pollPayload, discussionPayload] = await Promise.all([
        api<{ polls: CommunityPollRecord[] }>("/api/community/polls"),
        api<{ discussions: CommunityDiscussionRecord[] }>("/api/community/discussions")
      ]);
      setCommunityPolls(pollPayload.polls);
      setDiscussions(discussionPayload.discussions);
      setCommunityStatus(null);
    } catch (err) {
      setCommunityStatus((err as Error).message);
    } finally {
      setCommunityLoading(false);
    }
  }

  useEffect(() => {
    refreshCommunityPolls().catch(() => undefined);
  }, []);

  async function voteOnCommunityPoll(pollId: string, optionId: string) {
    if (!signedIn) {
      startJoin(undefined, "/polls");
      return;
    }
    setVoting(`${pollId}:${optionId}`);
    setCommunityStatus("Recording vote.");
    try {
      const payload = await api<{ poll: CommunityPollRecord }>(`/api/community/polls/${encodeURIComponent(pollId)}/vote`, {
        method: "POST",
        body: JSON.stringify({ optionId })
      });
      if (payload.poll) setCommunityPolls((current) => current.map((poll) => (poll.id === payload.poll.id ? payload.poll : poll)));
      setCommunityStatus("Vote recorded.");
    } catch (err) {
      setCommunityStatus((err as Error).message);
    } finally {
      setVoting(null);
    }
  }

  return (
    <main className="page polls-page">
      <SectionTitle
        icon={<Vote />}
        title="Polls People Can Rally Around"
        action="Moderated public votes"
        body="Vote on simple civic questions, then forward the result. Approved community polls become public signals people can act on."
      />
      <section className="cta-band poll-proposal">
        <div>
          <strong>Got a question your constituency keeps arguing about?</strong>
          <p>Turn it into a clean poll with 2 to 6 choices. We review proposals before they become public.</p>
        </div>
        <button className="primary" onClick={() => document.getElementById("poll-proposal-form")?.scrollIntoView({ block: "start", behavior: "smooth" })}><MessageSquare size={18} /> Start a poll</button>
      </section>
      <PollProposalForm signedIn={signedIn} discussions={discussions} onCreated={refreshCommunityPolls} />
      <section className="community-section">
        <div className="workspace-section-head">
          <div>
            <p className="eyebrow">Crowd-started polls</p>
            <h2>Questions people can forward</h2>
          </div>
          <span className="mini-chip active">{bootstrap?.counts.communityPolls ?? communityPolls.length} approved</span>
        </div>
        {communityStatus && <Status tone={communityStatus.includes("recorded") ? "success" : "info"} icon={<Vote />} title="Poll status" body={communityStatus} />}
        {communityLoading && <LoadingBlock title="Loading community polls" body="Fetching approved polls, vote totals, and attached discussions." />}
        {!communityLoading && communityPolls.length === 0 && <Status tone="info" icon={<Vote />} title="No community polls yet" body="Start the first clean question. Approved proposals will appear here." />}
        <div className="poll-grid community-poll-grid">
          {communityPolls.map((poll) => (
            <CommunityPollCard key={poll.id} poll={poll} signedIn={signedIn} voting={voting} onVote={voteOnCommunityPoll} />
          ))}
        </div>
      </section>
      <section className="community-section">
        <div className="workspace-section-head">
          <div>
            <p className="eyebrow">Issue votes</p>
            <h2>Live issues that need a clear signal</h2>
          </div>
          <button className="secondary" onClick={() => navigate("/sabha")}>View votes <ArrowRight size={16} /></button>
        </div>
        <div className="poll-grid" aria-label="Live issue votes">
          {issues.map((issue) => (
            <article key={issue.slug} className={`poll-card ${openingSlug === issue.slug ? "is-opening" : ""}`} aria-busy={openingSlug === issue.slug}>
              <span className="mini-chip active">{issue.category}</span>
              <h2>{issue.poll.question}</h2>
              <p>{issue.title}</p>
              <div className="poll-options-preview">
                {issue.poll.options.map((option) => <span key={option.id}>{option.label}</span>)}
              </div>
              <button className={`primary ${openingSlug === issue.slug ? "is-busy" : ""}`} onClick={() => openPoll(issue.slug)} disabled={Boolean(openingSlug)} aria-busy={openingSlug === issue.slug}>
                <BusyContent busy={openingSlug === issue.slug} busyLabel="Opening vote" icon={<Vote size={18} />}>Vote</BusyContent>
              </button>
            </article>
          ))}
          {issues.length === 0 && <Status tone="info" icon={<Sparkles />} title="Polls loading" body="Live issue votes are being fetched." />}
        </div>
      </section>
    </main>
  );
}

function PollProposalForm({ signedIn, discussions, onCreated }: { signedIn: boolean; discussions: CommunityDiscussionRecord[]; onCreated?: () => Promise<void> | void }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [discussionSlug, setDiscussionSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const updateOption = (index: number, value: string) => setOptions((current) => current.map((option, optionIndex) => (optionIndex === index ? value : option)));
  const addOption = () => setOptions((current) => (current.length >= 6 ? current : [...current, ""]));
  const removeOption = (index: number) => setOptions((current) => (current.length <= 2 ? current : current.filter((_, optionIndex) => optionIndex !== index)));

  async function submitPoll(event: FormEvent) {
    event.preventDefault();
    if (!signedIn) {
      startJoin(undefined, "/polls");
      return;
    }
    setSubmitting(true);
    setStatus("Sending your poll proposal for review.");
    try {
      await api<{ poll: CommunityPollRecord }>("/api/community/polls", {
        method: "POST",
        body: JSON.stringify({
          question: question.trim(),
          options: options.map((option) => option.trim()).filter(Boolean),
          discussionSlug: discussionSlug || undefined
        })
      });
      setQuestion("");
      setOptions(["", ""]);
      setDiscussionSlug("");
      setStatus("Poll proposal submitted. If approved, it will become a public vote people can share.");
      await onCreated?.();
    } catch (err) {
      setStatus((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form id="poll-proposal-form" className={`proposal-form ${submitting ? "is-processing" : ""}`} onSubmit={submitPoll} aria-busy={submitting}>
      <div className="panel-head">
        <div>
          <p className="eyebrow">Poll proposal</p>
          <h2>Write one question people can answer fast</h2>
        </div>
        <Vote size={18} />
      </div>
      <label htmlFor="poll-question">Question</label>
      <input id="poll-question" className="field" value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="Should our constituency support..." />
      <label htmlFor="poll-link-discussion">Connect to a topic</label>
      <select id="poll-link-discussion" className="field" value={discussionSlug} onChange={(event) => setDiscussionSlug(event.target.value)}>
        <option value="">Standalone poll</option>
        {discussions.map((discussion) => <option key={discussion.id} value={discussion.slug}>{discussion.title}</option>)}
      </select>
      <div className="option-editor">
        <span>Answer choices</span>
        {options.map((option, index) => (
          <div key={index} className="option-row">
            <input className="field" value={option} onChange={(event) => updateOption(index, event.target.value)} placeholder={`Option ${index + 1}`} />
            <button type="button" className="secondary" onClick={() => removeOption(index)} disabled={options.length <= 2}>Remove</button>
          </div>
        ))}
        <button type="button" className="secondary" onClick={addOption} disabled={options.length >= 6}>Add option</button>
      </div>
      <button className={`primary ${submitting ? "is-busy" : ""}`} type="submit" disabled={submitting || (signedIn && (!question.trim() || options.filter((option) => option.trim()).length < 2))}>
        <BusyContent busy={submitting} busyLabel="Submitting" icon={<ShieldCheck size={18} />}>{signedIn ? "Send for review" : "Sign in to propose"}</BusyContent>
      </button>
      {status && <Status tone={status.includes("submitted") ? "success" : "info"} icon={<ShieldCheck />} title="Proposal status" body={status} />}
    </form>
  );
}

function CommunityPollCard({ poll, signedIn, voting, onVote }: { poll: CommunityPollRecord; signedIn: boolean; voting: string | null; onVote: (pollId: string, optionId: string) => void }) {
  const hasVoted = Boolean(poll.userVoteOptionId);
  return (
    <article className="poll-card community-poll-card">
      <div className="bill-card-top">
        <span className="mini-chip active">{poll.status}</span>
        <small>{compactNumber(poll.totalVotes)} votes</small>
      </div>
      <h2>{poll.question}</h2>
      <p>{poll.discussionTitle ? `Linked to: ${poll.discussionTitle}` : "Standalone public poll"}</p>
      <div className="poll-vote-options">
        {poll.options.map((option) => {
          const activeVote = voting === `${poll.id}:${option.id}`;
          const selected = poll.userVoteOptionId === option.id;
          return (
            <button key={option.id} className={`poll-choice ${selected ? "selected" : ""} ${activeVote ? "is-busy" : ""}`} onClick={() => onVote(poll.id, option.id)} disabled={Boolean(voting) || hasVoted} aria-busy={activeVote}>
              <span><strong>{option.label}</strong><small>{option.percent}% / {compactNumber(option.voteCount)} votes</small></span>
              <i aria-hidden="true"><b style={{ width: `${option.percent}%` }} /></i>
            </button>
          );
        })}
      </div>
      {hasVoted ? <p className="panel-note success">Vote recorded. Share the result when the conversation needs evidence.</p> : <p className="panel-note">{signedIn ? "Choose one option. Results update immediately." : "Sign in to add weight to this poll."}</p>}
      {poll.discussionSlug && (
        <button className="secondary" onClick={() => navigate(`/discussions/${poll.discussionSlug}`)}><MessageSquare size={16} /> Join topic</button>
      )}
    </article>
  );
}

function CommunityDiscussionProposalForm({ signedIn, onCreated }: { signedIn: boolean; onCreated?: () => Promise<void> | void }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [includePoll, setIncludePoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const updateOption = (index: number, value: string) => setPollOptions((current) => current.map((option, optionIndex) => (optionIndex === index ? value : option)));
  const addOption = () => setPollOptions((current) => (current.length >= 6 ? current : [...current, ""]));
  const removeOption = (index: number) => setPollOptions((current) => (current.length <= 2 ? current : current.filter((_, optionIndex) => optionIndex !== index)));

  async function submitDiscussion(event: FormEvent) {
    event.preventDefault();
    if (!signedIn) {
      startJoin(undefined, "/community");
      return;
    }
    setSubmitting(true);
    setStatus("Sending topic proposal for review.");
    try {
      await api<{ discussion: CommunityDiscussionRecord }>("/api/community/discussions", {
        method: "POST",
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          poll: includePoll
            ? {
                question: pollQuestion.trim(),
                options: pollOptions.map((option) => option.trim()).filter(Boolean)
              }
            : undefined
        })
      });
      setTitle("");
      setBody("");
      setIncludePoll(false);
      setPollQuestion("");
      setPollOptions(["", ""]);
      setStatus("Topic proposal submitted. If approved, it will become a public debate people can share.");
      await onCreated?.();
    } catch (err) {
      setStatus((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form id="discussion-proposal-form" className={`proposal-form discussion-proposal-form ${submitting ? "is-processing" : ""}`} onSubmit={submitDiscussion} aria-busy={submitting}>
      <div className="panel-head">
        <div>
          <p className="eyebrow">Independent topic</p>
          <h2>Propose a topic worth forwarding</h2>
        </div>
        <MessageSquare size={18} />
      </div>
      <label htmlFor="discussion-title">Title</label>
      <input id="discussion-title" className="field" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="What should our constituency discuss?" />
      <label htmlFor="discussion-body">Opening note</label>
      <textarea id="discussion-body" value={body} onChange={(event) => setBody(event.target.value)} placeholder="State the problem, why now, and what kind of response would be useful." />
      <label className="privacy-toggle">
        <input type="checkbox" checked={includePoll} onChange={(event) => setIncludePoll(event.target.checked)} />
        <span>
          <strong>Add a poll to this topic</strong>
          <small>The topic and poll both become public only after admin approval.</small>
        </span>
      </label>
      {includePoll && (
        <div className="linked-poll-editor">
          <label htmlFor="discussion-poll-question">Poll question</label>
          <input id="discussion-poll-question" className="field" value={pollQuestion} onChange={(event) => setPollQuestion(event.target.value)} placeholder="What should people decide?" />
          <div className="option-editor">
            <span>Poll options</span>
            {pollOptions.map((option, index) => (
              <div key={index} className="option-row">
                <input className="field" value={option} onChange={(event) => updateOption(index, event.target.value)} placeholder={`Option ${index + 1}`} />
                <button type="button" className="secondary" onClick={() => removeOption(index)} disabled={pollOptions.length <= 2}>Remove</button>
              </div>
            ))}
            <button type="button" className="secondary" onClick={addOption} disabled={pollOptions.length >= 6}>Add option</button>
          </div>
        </div>
      )}
      <button className={`primary ${submitting ? "is-busy" : ""}`} type="submit" disabled={submitting || (signedIn && (!title.trim() || !body.trim()))}>
        <BusyContent busy={submitting} busyLabel="Submitting" icon={<ShieldCheck size={18} />}>{signedIn ? "Send for review" : "Sign in to propose"}</BusyContent>
      </button>
      {status && <Status tone={status.includes("submitted") ? "success" : "info"} icon={<ShieldCheck />} title="Proposal status" body={status} />}
    </form>
  );
}

function DiscussionListItem({ discussion }: { discussion: CommunityDiscussionRecord }) {
  return (
    <article className="discussion-topic" role="link" tabIndex={0} onClick={() => navigate(`/discussions/${discussion.slug}`)} onKeyDown={(event) => openOnKey(event, `/discussions/${discussion.slug}`)} aria-label={`Open ${discussion.title}`}>
      <div>
        <span className="mini-chip active">Independent</span>
        {discussion.poll && <span className="mini-chip">Poll attached</span>}
      </div>
      <h3>{discussion.title}</h3>
      <p>{discussion.body}</p>
      <div className="discussion-topic-meta">
        <span><MessageSquare size={15} /> {discussion.commentCount} arguments</span>
        <span><UsersRound size={15} /> {discussion.userLabel}</span>
      </div>
    </article>
  );
}

function DiscussionPage({ slug, bootstrap }: { slug: string; bootstrap: ApiState | null }) {
  const signedIn = Boolean(bootstrap?.user);
  const [discussion, setDiscussion] = useState<CommunityDiscussionRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [commentPending, setCommentPending] = useState(false);
  const [voting, setVoting] = useState<string | null>(null);

  async function refreshDiscussion() {
    setLoading(true);
    try {
      const payload = await api<{ discussion: CommunityDiscussionRecord }>(`/api/community/discussions/${encodeURIComponent(slug)}`);
      setDiscussion(payload.discussion);
      setStatus(null);
    } catch (err) {
      setStatus((err as Error).message);
      setDiscussion(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshDiscussion().catch(() => undefined);
  }, [slug]);

  async function voteOnPoll(pollId: string, optionId: string) {
    if (!signedIn) {
      startJoin(undefined, `/discussions/${slug}`);
      return;
    }
    setVoting(`${pollId}:${optionId}`);
    setStatus("Recording vote.");
    try {
      const payload = await api<{ poll: CommunityPollRecord }>(`/api/community/polls/${encodeURIComponent(pollId)}/vote`, {
        method: "POST",
        body: JSON.stringify({ optionId })
      });
      if (payload.poll) setDiscussion((current) => (current ? { ...current, poll: payload.poll } : current));
      setStatus("Vote recorded.");
    } catch (err) {
      setStatus((err as Error).message);
    } finally {
      setVoting(null);
    }
  }

  async function submitComment(event: FormEvent) {
    event.preventDefault();
    if (!signedIn) {
      startJoin(undefined, `/discussions/${slug}`);
      return;
    }
    if (!comment.trim()) {
      setStatus("Write one argument before posting.");
      return;
    }
    setCommentPending(true);
    setStatus("Posting argument.");
    try {
      const payload = await api<{ comment: DiscussionCommentRecord }>(`/api/community/discussions/${encodeURIComponent(slug)}/comments`, {
        method: "POST",
        body: JSON.stringify({ body: comment.trim() })
      });
      setDiscussion((current) => (current ? { ...current, comments: [...(current.comments ?? []), payload.comment], commentCount: current.commentCount + 1 } : current));
      setComment("");
      setStatus(`Argument ${payload.comment.status}.`);
    } catch (err) {
      setStatus((err as Error).message);
    } finally {
      setCommentPending(false);
    }
  }

  if (loading) return <Loading title="Opening topic" body="Loading the approved topic, poll, and arguments." />;
  if (!discussion) {
    return (
      <main className="page narrow">
        <button className="back-link" onClick={() => navigate("/community")}><ChevronLeft size={15} /> Back to Community</button>
        <Status tone="danger" icon={<AlertTriangle />} title="Topic unavailable" body={status ?? "Topic not found."} />
      </main>
    );
  }

  return (
    <main className="page discussion-page">
      <button className="back-link" onClick={() => navigate("/community")}><ChevronLeft size={15} /> Back to Community</button>
      <section className="discussion-titlebar">
        <div>
          <p className="eyebrow">Independent topic</p>
          <h1>{discussion.title}</h1>
          <p>{discussion.body}</p>
        </div>
        <div className="discussion-topic-meta">
          <span><UsersRound size={15} /> {discussion.userLabel}</span>
          <span><MessageSquare size={15} /> {discussion.commentCount} arguments</span>
        </div>
      </section>
      {status && <Status tone={status.includes("recorded") || status.includes("published") ? "success" : "info"} icon={<MessageSquare />} title="Topic status" body={status} />}
      <div className="discussion-layout">
        <section className="discussion-thread">
          <div className="workspace-section-head">
            <div>
              <p className="eyebrow">Public argument thread</p>
              <h2>Arguments</h2>
            </div>
          </div>
          <form className={`comment-form elevated ${commentPending ? "is-processing" : ""}`} onSubmit={submitComment} aria-busy={commentPending}>
            <label className="comment-box">
              <span>Your argument</span>
              <textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Add one useful view, source, or counterpoint." disabled={commentPending} />
            </label>
            <button className={`primary ${commentPending ? "is-busy" : ""}`} type="submit" disabled={commentPending || (signedIn && !comment.trim())}>
              <BusyContent busy={commentPending} busyLabel="Posting" icon={<MessageSquare size={18} />}>{signedIn ? "Post argument" : "Sign in to argue"}</BusyContent>
            </button>
          </form>
          <div className="comments">
            {(discussion.comments ?? []).length === 0 && <Status tone="info" icon={<MessageSquare />} title="No arguments yet" body="Be the first to add a useful point to this topic." />}
            {(discussion.comments ?? []).map((item) => (
              <article key={item.id} className="comment discussion-card">
                <div className="comment-head">
                  <strong>{item.userLabel}</strong>
                  <small>{item.status}</small>
                </div>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>
        <aside className="workspace-rail sticky-panel">
          {discussion.poll ? (
            <CommunityPollCard poll={discussion.poll} signedIn={signedIn} voting={voting} onVote={voteOnPoll} />
          ) : (
            <section className="rail-card">
              <div className="rail-card-head">
                <h2>No poll attached</h2>
                <Vote size={18} />
              </div>
              <p className="fine-print">This topic is open for arguments only. A separate poll can be proposed from the Polls page.</p>
              <button className="secondary full" onClick={() => navigate("/polls")}>Propose poll <ArrowRight size={15} /></button>
            </section>
          )}
          <section className="rail-card">
            <h2>Approval status</h2>
            <div className="guideline-list">
              <span><ShieldCheck size={16} /> Topic approved by admin</span>
              <span><Vote size={16} /> Polls require approval before voting</span>
              <span><MessageSquare size={16} /> Arguments remain moderated</span>
            </div>
          </section>
        </aside>
      </div>
    </main>
  );
}

function PulsePage({ bootstrap, issues }: { bootstrap: ApiState | null; issues: Issue[] }) {
  const [report, setReport] = useState<TransparencyReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ reports: TransparencyReport[] }>("/api/transparency/reports")
      .then((payload) => setReport(payload.reports[0] ?? null))
      .catch((err: Error) => setError(err.message));
  }, []);

  const stats = [
    ["Constituencies", bootstrap?.counts.constituencies ?? 543],
    ["Representatives", bootstrap?.counts.representatives ?? 0],
    ["Votes cast", report?.votesCast ?? bootstrap?.counts.votes ?? 0],
    ["Arguments", report?.commentsPosted ?? bootstrap?.counts.comments ?? 0],
    ["Share cards", report?.shareCardsGenerated ?? 0],
    ["Live issues", issues.length || bootstrap?.counts.issues || 0]
  ];

  return (
    <main className="page pulse-page">
      <SectionTitle
        icon={<LineChart />}
        title="Civic Pulse"
        action={report ? "Live signal" : "Loading"}
        body="The public scoreboard for Roachsabha activity: votes, arguments, share cards, and constituency reach."
      />
      {error && <Status tone="danger" icon={<AlertTriangle />} title="Pulse unavailable" body={error} />}
      <section className="pulse-grid" aria-label="Civic pulse metrics">
        {stats.map(([label, value]) => (
          <article key={label} className="pulse-card">
            <small>{label}</small>
            <strong>{typeof value === "number" ? compactNumber(value) : value}</strong>
          </article>
        ))}
      </section>
      <section className="two-col wide">
        <div className="rail-card">
          <div className="rail-card-head">
            <h2>Votes moving right now</h2>
            <button className="secondary" onClick={() => navigate("/sabha")}>Open all votes <ArrowRight size={15} /></button>
          </div>
          <div className="discussion-list">
            {issues.slice(0, 6).map((issue) => (
              <button key={issue.slug} onClick={() => navigate(`/issues/${issue.slug}`)}>
                <FileText size={18} />
                <span><strong>{issue.title}</strong><small>{issue.category} / {issue.status}</small></span>
              </button>
            ))}
          </div>
        </div>
        <div className="rail-card">
          <h2>Why the numbers are shareable</h2>
          <div className="guideline-list">
            <span><Scale size={16} /> Advisory and non-binding, never official election data</span>
            <span><ShieldCheck size={16} /> Public results are aggregated before display</span>
            <span><LockKeyhole size={16} /> No caste, religion, phone, or Aadhaar collection in v1</span>
            <span><FileText size={16} /> Audit root: {report?.auditRoot ?? "Pending report load"}</span>
          </div>
        </div>
      </section>
    </main>
  );
}

function Join({ initialBootstrap }: { initialBootstrap: ApiState | null }) {
  const [bootstrap, setBootstrap] = useState<ApiState | null>(initialBootstrap);
  const [status, setStatus] = useState<string | null>(null);
  const [statusTone, setStatusTone] = useState<"info" | "success" | "warning" | "danger">("info");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [ageBracket, setAgeBracket] = useState("18_25");
  const [anonymousMode, setAnonymousMode] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Swarm[]>([]);
  const [selected, setSelected] = useState<Swarm | null>(null);
  const [magicPending, setMagicPending] = useState(false);
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [returnTo] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return safeReturnPath(params.get("returnTo")) ?? safeReturnPath(storageGet(pendingReturnToStorageKey)) ?? "/sabha";
  });
  const [pendingSwarmSlug] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("swarm") ?? storageGet(pendingSwarmStorageKey);
  });

  useEffect(() => {
    let active = true;
    if (!initialBootstrap) return () => {
      active = false;
    };

    Promise.resolve(initialBootstrap)
      .then((payload) => {
        if (!active) return;
        setBootstrap(payload);
        setDisplayName(payload.user?.displayName ?? "");
        setAgeBracket(payload.user?.ageBracket ?? "18_25");
        setAnonymousMode(Boolean(payload.user?.anonymousMode));
        if (payload.user?.homeSwarmSlug) {
          api<{ swarm: Swarm; representative?: Representative }>(`/api/swarms/${payload.user.homeSwarmSlug}`)
            .then((swarmPayload) => setSelected({ ...swarmPayload.swarm, representative: swarmPayload.representative }))
            .catch(() => undefined);
        } else if (pendingSwarmSlug) {
          api<{ swarm: Swarm; representative?: Representative }>(`/api/swarms/${pendingSwarmSlug}`)
            .then((swarmPayload) => {
              const swarm = { ...swarmPayload.swarm, representative: swarmPayload.representative };
              setSelected(swarm);
              setResults([swarm]);
              setQuery(swarm.name);
              rememberJoinIntent(swarm.slug, returnTo);
            })
            .catch(() => search(payload.geo?.searchQuery || "Bangalore").catch(() => undefined));
        } else if (payload.user) {
          search(payload.geo?.searchQuery || "Bangalore").catch(() => undefined);
        }
      })
      .catch((err: Error) => {
        if (!active) return;
        setStatusTone("danger");
        setStatus(err.message);
      });
    return () => {
      active = false;
    };
  }, [initialBootstrap, pendingSwarmSlug, returnTo]);

  async function search(value: string) {
    setQuery(value);
    if (!value.trim()) {
      setResults([]);
      return;
    }
    const payload = await api<{ results: Swarm[] }>(`/api/swarms/search?q=${encodeURIComponent(value)}`);
    setResults(payload.results);
  }

  async function startGoogle() {
    rememberJoinIntent(selected?.slug ?? pendingSwarmSlug, returnTo);
    const params = new URLSearchParams();
    if (selected?.slug ?? pendingSwarmSlug) params.set("swarm", selected?.slug ?? pendingSwarmSlug ?? "");
    if (returnTo !== "/sabha") params.set("returnTo", returnTo);
    const queryString = params.toString();
    window.location.href = `/api/auth/google/start${queryString ? `?${queryString}` : ""}`;
  }

  async function requestMagicLink(event: FormEvent) {
    event.preventDefault();
    rememberJoinIntent(selected?.slug ?? pendingSwarmSlug, returnTo);
    setMagicPending(true);
    try {
      const payload = await api<{ delivered: boolean; devLink?: string }>("/api/auth/magic-link/request", {
        method: "POST",
        body: JSON.stringify({ email, swarmSlug: selected?.slug ?? pendingSwarmSlug, returnTo })
      });
      if (payload.devLink) {
        window.location.href = payload.devLink;
        return;
      }
      setStatusTone("success");
      setStatus(payload.delivered ? "Magic link sent. Check your email to finish sign-in." : "Magic link request accepted.");
    } catch (err) {
      setStatusTone("danger");
      setStatus((err as Error).message);
    } finally {
      setMagicPending(false);
    }
  }

  async function saveOnboarding(event: FormEvent) {
    event.preventDefault();
    if (!selected) {
      setStatusTone("warning");
      setStatus("Choose your Lok Sabha constituency before saving.");
      return;
    }
    setSavingOnboarding(true);
    try {
      const payload = await api<{ user: ApiState["user"] }>("/api/onboarding", {
        method: "POST",
        body: JSON.stringify({ swarmSlug: selected.slug, ageBracket, displayName, anonymousMode })
      });
      setBootstrap((current) => (current ? { ...current, user: payload.user } : current));
      clearJoinIntent();
      setStatusTone("success");
      setStatus(payload.user?.role === "observer" ? "Constituency saved. Observer accounts can read, discuss, and share, but cannot vote." : "Constituency saved. Opening your next action...");
      setTimeout(() => {
        window.location.href = returnTo;
      }, 450);
    } catch (err) {
      setStatusTone("danger");
      setStatus((err as Error).message);
    } finally {
      setSavingOnboarding(false);
    }
  }

  const signedIn = Boolean(bootstrap?.user);
  const selectedStatusBody = signedIn
    ? "We store age bracket and consent so votes stay eligible. No Aadhaar, phone, caste, religion, or party preference is collected in v1."
    : "We will remember this seat through sign-in so you can continue without searching again.";

  return (
    <main className="page two-col">
      <section>
        <p className="eyebrow">Join the signal</p>
        <h1>Choose the seat your vote should strengthen.</h1>
        <p className="lede small">Roachsabha works because each vote has a constituency context. Pick your Lok Sabha seat once, then vote and share local signals.</p>
        <div className="step-list" aria-label="Onboarding progress">
          <span className={signedIn ? "done" : "active"}>Sign in</span>
          <span className={selected ? "done" : signedIn ? "active" : ""}>Choose constituency</span>
          <span className={bootstrap?.user?.onboardingComplete ? "done" : selected ? "active" : ""}>Save</span>
        </div>
        {!signedIn ? (
          <div className="stack">
            <button className="primary" onClick={startGoogle} title="Start Google OAuth">
              <ShieldCheck size={18} /> Continue with Google
            </button>
            <form className="inline-form" onSubmit={requestMagicLink}>
              <label htmlFor="join-email">Email magic link</label>
              <div className="search-row">
                <LockKeyhole size={18} />
                <input id="join-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" required />
              </div>
              <button className="secondary full" type="submit" title="Request email magic link" disabled={magicPending}>
                <LockKeyhole size={18} /> {magicPending ? "Sending..." : "Send magic link"}
              </button>
            </form>
            {status && <Status tone={statusTone} icon={<Sparkles />} title="Sign-in status" body={status} />}
          </div>
        ) : (
          <div className="stack">
            <Status
              tone={bootstrap?.user?.onboardingComplete ? "success" : "info"}
              icon={<BadgeCheck />}
              title={bootstrap?.user?.onboardingComplete ? "Signed in" : "Finish onboarding"}
              body={bootstrap?.user?.onboardingComplete ? `${bootstrap.user.displayName} is assigned to ${selected?.name ?? bootstrap.user.homeSwarmSlug}.` : selected ? "Confirm your age bracket and open voting." : "Select your constituency and age bracket to unlock voting, arguments, and share cards."}
            />
            <button
              className="secondary"
              onClick={() =>
                api("/api/auth/logout", { method: "POST", body: "{}" })
                  .then(() => window.location.reload())
                  .catch((err: Error) => {
                    setStatusTone("danger");
                    setStatus(err.message);
                  })
              }
              title="Sign out"
            >
              <LockKeyhole size={18} /> Sign out
            </button>
            {status && <Status tone={statusTone} icon={<Sparkles />} title="Onboarding status" body={status} />}
          </div>
        )}
      </section>
      <form className="tool-panel" onSubmit={saveOnboarding}>
        <div className="panel-head">
          <div>
            <p className="eyebrow">Public profile</p>
            <h2>Set up voting</h2>
          </div>
          <BadgeCheck size={18} />
        </div>
        <label htmlFor="join-display-name">Display name</label>
        <input
          id="join-display-name"
          className="field"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="Public display name"
          disabled={!signedIn}
        />
        <label htmlFor="join-age">Age bracket</label>
        <select id="join-age" className="field" value={ageBracket} onChange={(event) => setAgeBracket(event.target.value)} disabled={!signedIn}>
          <option value="14_17">14-17 observer</option>
          <option value="18_25">18-25</option>
          <option value="26_35">26-35</option>
          <option value="36_50">36-50</option>
          <option value="51_plus">51+</option>
        </select>
        <label className="privacy-toggle">
          <input type="checkbox" checked={anonymousMode} onChange={(event) => setAnonymousMode(event.target.checked)} disabled={!signedIn} />
          <span>
            <strong>Stay anonymous publicly</strong>
            <small>Show "Anonymous citizen" in public arguments and member lists.</small>
          </span>
        </label>
        <label htmlFor="join-swarm-search">Constituency selection</label>
        <div className="search-row">
          <Search size={18} />
          <input id="join-swarm-search" value={query} onChange={(event) => search(event.target.value)} placeholder="Try Delhi, Pune, Bangalore" />
        </div>
        <div className="swarm-results fixed">
          {query && results.length === 0 && <p className="panel-note">No constituency found for this search.</p>}
          {results.map((swarm) => (
            <button
              key={swarm.slug}
              type="button"
              className={`result-row ${selected?.slug === swarm.slug ? "selected" : ""}`}
              onClick={() => {
                setSelected(swarm);
                rememberJoinIntent(swarm.slug, returnTo);
              }}
            >
              <RepresentativePhoto rep={swarm.representative} size="small" />
              <span className="result-main">
                <strong>{swarm.name}</strong>
                <small>{swarm.representative ? `${swarm.representative.name} / ${swarm.representative.partyShort}` : swarm.state}{typeof swarm.signupCount === "number" ? ` / ${compactNumber(swarm.signupCount)} signed up` : ""}</small>
              </span>
              <BadgeCheck size={16} />
            </button>
          ))}
        </div>
        {selected && (
          <Status
            tone="success"
            icon={<BadgeCheck />}
            title={`${selected.name} selected`}
            body={selectedStatusBody}
          />
        )}
        <button className="primary full" type="submit" disabled={!signedIn || !selected || savingOnboarding}>
          <BadgeCheck size={18} /> {savingOnboarding ? "Saving..." : "Save and start voting"}
        </button>
      </form>
    </main>
  );
}

function AccountPage({ bootstrap, onUserUpdate }: { bootstrap: ApiState | null; onUserUpdate: (user: ApiState["user"]) => void }) {
  const user = bootstrap?.user;
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [anonymousMode, setAnonymousMode] = useState(Boolean(user?.anonymousMode));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    setDisplayName(user?.displayName ?? "");
    setAnonymousMode(Boolean(user?.anonymousMode));
  }, [user?.displayName, user?.anonymousMode]);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setStatus(null);
    try {
      const payload = await api<{ user: ApiState["user"] }>("/api/account", {
        method: "POST",
        body: JSON.stringify({ displayName, anonymousMode })
      });
      onUserUpdate(payload.user);
      setStatus("Account preferences saved.");
    } catch (err) {
      setStatus((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteAccount(event: FormEvent) {
    event.preventDefault();
    if (!deleteConfirmed) {
      setStatus("Confirm account deletion before continuing.");
      return;
    }
    if (!window.confirm("Delete your account? Your profile and sign-in access will be removed. Votes and arguments will remain.")) return;
    setDeleting(true);
    setStatus(null);
    try {
      await api<{ retained: { votes: boolean; comments: boolean } }>("/api/account", {
        method: "DELETE",
        body: "{}"
      });
      setDeleted(true);
      setDeleteConfirmed(false);
      setDisplayName("");
      setAnonymousMode(false);
      onUserUpdate(null);
      setStatus("Account deleted. Votes, arguments, and civic records remain without your account details.");
    } catch (err) {
      setStatus((err as Error).message);
    } finally {
      setDeleting(false);
    }
  }

  if (!user) {
    return (
      <main className="page narrow account-page">
        <SectionTitle
          icon={<BadgeCheck />}
          title="Your Account"
          action={deleted ? "Deleted" : "Sign in required"}
          body={deleted ? "Your sign-in identity and profile have been removed." : "Sign in to manage your public name, constituency, and privacy settings."}
        />
        {deleted ? (
          <Status tone="success" icon={<ShieldCheck />} title="Account deleted" body={status ?? "Votes, arguments, and civic records remain without your account details."} />
        ) : (
          <>
            <Status tone="warning" icon={<LockKeyhole />} title="No active session" body="Account settings appear after you sign in." />
            <button className="primary" onClick={() => startJoin(undefined, "/account")}><ShieldCheck size={18} /> Sign in</button>
          </>
        )}
      </main>
    );
  }

  const homeSwarmLabel = user.homeSwarmSlug?.replaceAll("-", " ") ?? "Not selected";

  return (
    <main className="page account-page">
      <SectionTitle icon={<BadgeCheck />} title="Your Account" action={user.role} body="Control how you appear in public arguments, member lists, and constituency signals." />
      <section className="account-grid">
        <form className="tool-panel" onSubmit={saveProfile}>
          <div className="panel-head">
            <div>
              <p className="eyebrow">Profile</p>
              <h2>Public identity</h2>
            </div>
            <BadgeCheck size={18} />
          </div>
          <label htmlFor="account-display-name">Display name</label>
          <input id="account-display-name" className="field" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
          <label className="privacy-toggle">
            <input type="checkbox" checked={anonymousMode} onChange={(event) => setAnonymousMode(event.target.checked)} />
            <span>
              <strong>Stay anonymous publicly</strong>
              <small>Arguments and public member lists will show "Anonymous citizen" instead of your name or constituency details.</small>
            </span>
          </label>
          <button className="primary full" type="submit" disabled={saving}><BadgeCheck size={18} /> {saving ? "Saving..." : "Save account settings"}</button>
          {status && <Status tone={status.includes("saved") ? "success" : "danger"} icon={<Radio />} title="Account status" body={status} />}
        </form>

        <section className="rail-card account-card">
          <div className="rail-card-head">
            <h2>My constituency</h2>
            <UsersRound size={18} />
          </div>
          <p><strong>{homeSwarmLabel}</strong></p>
          <p className="fine-print">Your constituency powers local vote results, discussion context, and shareable signal pages.</p>
          {user.homeSwarmSlug ? (
            <button className="primary full" onClick={() => navigate(`/swarms/${user.homeSwarmSlug}`)}><LineChart size={18} /> Open my constituency</button>
          ) : (
            <button className="primary full" onClick={() => startJoin(undefined, "/account")}><Search size={18} /> Choose my constituency</button>
          )}
          <div className="account-facts">
            <span><strong>{user.ageBracket.replace("_", "-")}</strong><small>Age bracket</small></span>
            <span><strong>{user.anonymousMode ? "On" : "Off"}</strong><small>Anonymous mode</small></span>
            <span><strong>{user.onboardingComplete ? "Complete" : "Pending"}</strong><small>Onboarding</small></span>
          </div>
        </section>

        <form className="tool-panel danger-zone" onSubmit={deleteAccount}>
          <div className="panel-head">
            <div>
              <p className="eyebrow">Account deletion</p>
              <h2>Delete account</h2>
            </div>
            <AlertTriangle size={18} />
          </div>
          <p className="fine-print">This removes your sign-in identity, sessions, email link, display name, age bracket, and constituency assignment. Votes, arguments, poll/topic proposals, and moderation records remain as retained civic history.</p>
          <label className="privacy-toggle delete-confirmation">
            <input type="checkbox" checked={deleteConfirmed} onChange={(event) => setDeleteConfirmed(event.target.checked)} />
            <span>
              <strong>I understand retained activity will stay</strong>
              <small>Your public label on retained activity changes to "Deleted account".</small>
            </span>
          </label>
          <button className={`danger full ${deleting ? "is-busy" : ""}`} type="submit" disabled={deleting || !deleteConfirmed}>
            <Trash2 size={18} /> {deleting ? "Deleting..." : "Delete my account"}
          </button>
        </form>
      </section>
    </main>
  );
}

function Sabha({ issues, bootstrap }: { issues: Issue[]; bootstrap: ApiState | null }) {
  const onboarded = Boolean(bootstrap?.user?.onboardingComplete);
  return (
    <main className="page">
      <SectionTitle
        icon={<Landmark />}
        title="Issues in Public Vote"
        action={bootstrap?.user?.homeSwarmSlug?.replaceAll("-", " ") ?? "Guest mode"}
        body="Pick a live issue, vote your stance, and turn the result into a shareable constituency signal."
      />
      {!onboarded && (
        <div className="cta-band">
          <div>
            <strong>Choose your constituency to make the vote count locally</strong>
            <p>Guests can read every brief. Voting, arguments, and share cards unlock after sign-in and constituency selection.</p>
          </div>
          <button className="primary" onClick={() => startJoin(undefined, "/sabha")}><BadgeCheck size={18} /> Join and vote</button>
        </div>
      )}
      <div className="dashboard-grid">
        <Status tone="info" icon={<Scale />} title="Public, not official" body="Results are civic signals, not official polls, legal verdicts, or election results." />
        <Status tone="success" icon={<ShieldCheck />} title="Private individual votes" body="Your vote is not shown. Public results appear only as aggregated issue and constituency counts." />
        <Status tone="warning" icon={<Bot />} title="Human review before delivery" body="AI summaries help comparison, but sensitive language and mandates require human review." />
      </div>
      <div className="issue-list">
        {issues.map((issue) => (
          <IssueRow key={issue.slug} issue={issue} />
        ))}
        {issues.length === 0 && <Status tone="info" icon={<Sparkles />} title="Issues loading" body="Live issue briefs are being fetched." />}
      </div>
    </main>
  );
}

function IssuePage({ path, issues, bootstrap }: { path: string; issues: Issue[]; bootstrap: ApiState | null }) {
  const slug = path.split("/")[2];
  const discuss = path.endsWith("/discuss");
  const issue = issues.find((item) => item.slug === slug);
  const [results, setResults] = useState<Results | null>(null);
  const [breakdown, setBreakdown] = useState<HeatMapRow[] | null>(null);
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [position, setPosition] = useState<StanceKey | null>(null);
  const [userVote, setUserVote] = useState<UserPollVote | null>(null);
  const [comment, setComment] = useState("");
  const [voteStatus, setVoteStatus] = useState<string | null>(null);
  const [discussionStatus, setDiscussionStatus] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [votePending, setVotePending] = useState<string | null>(null);
  const [commentPending, setCommentPending] = useState(false);
  const [sharePending, setSharePending] = useState(false);
  const [upvotePendingId, setUpvotePendingId] = useState<string | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [userVoteLoading, setUserVoteLoading] = useState(false);

  useEffect(() => {
    if (!issue) return;
    let active = true;
    const swarm = bootstrap?.user?.homeSwarmSlug ? `?swarm=${encodeURIComponent(bootstrap.user.homeSwarmSlug)}` : "";
    setResults(null);
    setBreakdown(null);
    setComments([]);
    setUserVote(null);
    setVoteStatus(null);
    setResultsLoading(true);
    setBreakdownLoading(true);
    setCommentsLoading(true);
    setUserVoteLoading(Boolean(bootstrap?.user));
    api<Results>(`/api/issues/${issue.slug}/results${swarm}`)
      .then((payload) => {
        if (active) setResults(payload);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setResultsLoading(false);
      });
    api<{ breakdown: HeatMapRow[] }>(`/api/issues/${issue.slug}/breakdown`)
      .then((payload) => {
        if (active) setBreakdown(payload.breakdown);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setBreakdownLoading(false);
      });
    api<{ comments: CommentRecord[] }>(`/api/comments?issueId=${issue.id}`)
      .then((payload) => {
        if (active) setComments(payload.comments);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setCommentsLoading(false);
      });
    if (bootstrap?.user) {
      api<{ userVote: UserPollVote | null }>(`/api/polls/${issue.poll.id}/vote`)
        .then((payload) => {
          if (active) setUserVote(payload.userVote);
        })
        .catch(() => undefined)
        .finally(() => {
          if (active) setUserVoteLoading(false);
        });
    }
    return () => {
      active = false;
    };
  }, [bootstrap?.user?.homeSwarmSlug, bootstrap?.user?.id, issue]);

  if (!issue && issues.length === 0) return <Loading title="Opening vote" body="Loading the issue brief, ballot, and current signal." />;
  if (!issue) return <NotFound />;
  const activeIssue = issue;

  async function vote(optionId: StanceKey) {
    if (votePending) return;
    setVotePending(optionId);
    setVoteStatus("Submitting vote. Your private ballot is being added to the public signal.");
    try {
      const payload = await api<Results & { voteId: string; userVote: UserPollVote | null }>(`/api/polls/${activeIssue.poll.id}/vote`, {
        method: "POST",
        body: JSON.stringify({ optionId })
      });
      setVoteStatus(`Vote accepted. Receipt ${payload.voteId}. Share the signal to bring more voices from your seat.`);
      setUserVote(payload.userVote ?? { optionId, swarmId: bootstrap?.user?.homeSwarmSlug ?? "", votedAt: new Date().toISOString() });
      setResults(payload);
    } catch (err) {
      const message = (err as Error).message;
      setVoteStatus(message);
      if (/already voted/i.test(message) && bootstrap?.user) {
        const payload = await api<{ userVote: UserPollVote | null }>(`/api/polls/${activeIssue.poll.id}/vote`).catch(() => null);
        if (payload?.userVote) setUserVote(payload.userVote);
      }
    } finally {
      setVotePending(null);
    }
  }

  async function submitComment(event: FormEvent) {
    event.preventDefault();
    if (!position) {
      setDiscussionStatus("Choose support, needs reform, or oppose before posting.");
      return;
    }
    if (!comment.trim()) {
      setDiscussionStatus("Write one clear point before posting.");
      return;
    }
    setCommentPending(true);
    setDiscussionStatus("Posting argument. Your point is being sent to moderation.");
    try {
      const payload = await api<{ comment: CommentRecord }>("/api/comments", {
        method: "POST",
        body: JSON.stringify({ issueSlug: activeIssue.slug, position, body: comment.trim() })
      });
      setComments((current) => [payload.comment, ...current]);
      setComment("");
      setPosition(null);
      setDiscussionStatus(`Argument ${payload.comment.status}. Strong arguments help the mandate improve.`);
    } catch (err) {
      setDiscussionStatus((err as Error).message);
    } finally {
      setCommentPending(false);
    }
  }

  async function shareCard() {
    if (sharePending) return;
    const swarmSlug = bootstrap?.user?.homeSwarmSlug;
    if (!swarmSlug) {
      setVoteStatus("Choose your constituency before generating a share card.");
      return;
    }
    setSharePending(true);
    setVoteStatus("Generating a share card your friends can understand at a glance.");
    try {
      const payload = await api<{ previewUrl: string }>("/api/share-cards", {
        method: "POST",
        body: JSON.stringify({ issueSlug: activeIssue.slug, swarmSlug, format: "square" })
      });
      setShareUrl(payload.previewUrl);
      setVoteStatus("Share card ready. Send it where the issue is already being discussed.");
    } catch (err) {
      setVoteStatus((err as Error).message);
    } finally {
      setSharePending(false);
    }
  }

  async function copyShareCardLink() {
    if (!shareUrl) return;
    try {
      await copyToClipboard(absoluteAppUrl(shareUrl));
      setVoteStatus("Share card link copied.");
    } catch (err) {
      setVoteStatus((err as Error).message);
    }
  }

  async function upvoteComment(id: string) {
    if (upvotePendingId) return;
    setUpvotePendingId(id);
    setDiscussionStatus("Recording upvote.");
    try {
      await api(`/api/comments/${encodeURIComponent(id)}/react`, { method: "POST", body: "{}" });
      setComments((current) => current.map((item) => (item.id === id ? { ...item, upvotes: (item.upvotes ?? 0) + 1 } : item)));
      setDiscussionStatus("Upvote recorded.");
    } catch (err) {
      setDiscussionStatus((err as Error).message);
    } finally {
      setUpvotePendingId(null);
    }
  }

  const canParticipate = Boolean(bootstrap?.user?.onboardingComplete && bootstrap.user.role === "participant");
  const canComment = Boolean(bootstrap?.user);
  const needsOnboarding = !bootstrap?.user?.onboardingComplete;
  const supportSignal = results?.swarmResult.support ?? results?.nationalResult.support ?? 0;
  const activeVotes = results?.swarmVotes ?? results?.totalVotes ?? 0;
  const activeSwarm = breakdown?.find((row) => row.swarm.slug === bootstrap?.user?.homeSwarmSlug)?.swarm;
  const activeSwarmLabel = activeSwarm ? `${activeSwarm.name}, ${activeSwarm.state}` : bootstrap?.user?.homeSwarmSlug?.replaceAll("-", " ") ?? "Guest mode";
  const boundedSupportSignal = Math.max(0, Math.min(100, supportSignal));
  const signalBreakdown = issue.poll.options.map((option) => ({
    id: option.id,
    label: stanceLabels[option.id],
    value: Math.max(0, Math.min(100, results?.swarmResult[option.id] ?? 0))
  }));
  const selectedPositionLabel = position ? stanceLabels[position] : null;
  const castedVoteOption = userVote ? issue.poll.options.find((option) => option.id === userVote.optionId) : null;
  const castedVoteLabel = castedVoteOption?.label ?? (userVote ? stanceLabels[userVote.optionId] : "");
  const castedVoteTime = userVote ? new Date(userVote.votedAt).toLocaleString() : "";
  const reviewFlags = Array.from(new Set(Object.values(issue.aiSummary.positions).flatMap((summary) => summary.reviewFlags ?? [])));

  return (
    <main className="page workspace-page">
      <button className="back-link" onClick={() => navigate("/sabha")}>
        <ChevronLeft size={15} /> Back to votes
      </button>

      <div className="workspace-titlebar">
        <div>
          <div className="title-meta">
            <span>{issue.category}</span>
            <span>{issue.riskLevel}</span>
            <span>{activeSwarmLabel}</span>
          </div>
          <h1>{issue.title}</h1>
          <p>{issue.brief}</p>
        </div>
        <div className="workspace-actions">
          <span className="status-pill"><CircleDot size={14} /> In public vote</span>
          <button className={`secondary ${sharePending ? "is-busy" : ""}`} onClick={shareCard} disabled={sharePending} aria-busy={sharePending}>
            <BusyContent busy={sharePending} busyLabel="Generating" icon={<Share2 size={17} />}>Make share card</BusyContent>
          </button>
          <button className="primary" onClick={() => (needsOnboarding ? startJoin(undefined, currentAppPath()) : navigate(`/issues/${issue.slug}`))}>
            <Vote size={17} /> {needsOnboarding ? "Join to vote" : "Cast vote"}
          </button>
        </div>
      </div>

      <section className="workspace-poster-row">
        <IssuePoster issue={issue} />
      </section>

      <IssueWorkflow />

      {reviewFlags.length > 0 && (
        <Status tone="warning" icon={<ShieldCheck />} title="Legal care required" body={reviewFlags.join(" ")} />
      )}

      <div className="tabs segment-tabs" role="tablist" aria-label="Issue sections">
        <button className={!discuss ? "active" : ""} onClick={() => navigate(`/issues/${issue.slug}`)}>Brief and vote</button>
        <button className={discuss ? "active" : ""} onClick={() => navigate(`/issues/${issue.slug}/discuss`)}>Arguments</button>
      </div>

      <div className="workspace-grid">
        <section className="workspace-main">
          {!discuss ? (
            <>
              <div className="workspace-section-head">
                <div>
                  <h2>Why this vote matters</h2>
                  <p>Review the stake, the source material, and the exact ballot language before you send a signal.</p>
                </div>
                <div className="compact-actions">
                  <button className="secondary" onClick={() => navigate(`/issues/${issue.slug}/discuss`)}><MessageSquare size={16} /> Read arguments</button>
                </div>
              </div>
              <div className="clause-stack">
                <article className="mandate-clause">
                  <div className="clause-label">Clause 1</div>
                  <div className="clause-body">
                    <span className="clause-tag">Core</span>
                    <h3>The public stake</h3>
                    <p>{issue.whyItMatters}</p>
                    <div className="clause-stats">
                      <span><strong>{supportSignal}%</strong> Support</span>
                      <span><strong>{activeVotes.toLocaleString()}</strong> Votes</span>
                      <span><strong>{results?.constituenciesRepresented ?? 0}</strong> Constituencies</span>
                    </div>
                  </div>
                </article>
                <article className="mandate-clause">
                  <div className="clause-label">Clause 2</div>
                  <div className="clause-body">
                    <span className="clause-tag">Ballot</span>
                    <h3>{issue.poll.question}</h3>
                    <p>Your vote strengthens your constituency signal and is never shown individually.</p>
                    <div className="clause-stats">
                      <span><strong>{issue.poll.options.length}</strong> Options</span>
                      <span><strong>{issue.poll.status}</strong> Status</span>
                      <span><strong>{results ? new Date(results.updatedAt).toLocaleTimeString() : "Pending"}</strong> Updated</span>
                    </div>
                  </div>
                </article>
                <article className="mandate-clause">
                  <div className="clause-label">Sources</div>
                  <div className="clause-body">
                    <span className="clause-tag">Reference</span>
                    <h3>Receipts behind the vote</h3>
                    <ul className="doc-list compact">
                      {issue.documents.map((doc) => {
                        const source = issueDocumentMeta(doc);
                        return (
                          <li key={source.url || source.title}>
                            <FileText size={16} />
                            <span className="source-doc-copy">
                              {source.url ? (
                                <a className="source-doc-link" href={source.url} target="_blank" rel="noreferrer">
                                  {source.title} <ExternalLink size={13} />
                                </a>
                              ) : (
                                <span>{source.title}</span>
                              )}
                              {source.source && <small>{source.source}</small>}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </article>
              </div>
            </>
          ) : (
            <>
              <div className="workspace-section-head">
                <div>
                  <h2>Arguments people can reuse</h2>
                  <p>Add one concise, evidence-aware point tied to the stance it supports.</p>
                </div>
              </div>
              <form className={`comment-form elevated ${commentPending ? "is-processing" : ""}`} onSubmit={submitComment} aria-busy={commentPending}>
                <fieldset className="stance-radio-field" disabled={commentPending}>
                  <legend>Your position</legend>
                  <div className="stance-radio-list">
                    {activeIssue.poll.options.map((option) => (
                      <label key={option.id} className={`stance-radio-card stance-${option.id} ${position === option.id ? "selected" : ""}`}>
                        <input type="radio" name="comment-position" value={option.id} checked={position === option.id} onChange={() => setPosition(option.id)} />
                        <span className="radio-mark" aria-hidden="true" />
                        <span className="stance-copy">
                          <strong>{stanceLabels[option.id]}</strong>
                          <small>{option.label}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label className={`comment-box ${position ? "is-ready" : ""}`}>
                  <span>{selectedPositionLabel ? `${selectedPositionLabel} argument` : "Argument"}</span>
                  <textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder={selectedPositionLabel ? `Write one ${selectedPositionLabel.toLowerCase()} point others can quote.` : "Select a position to begin."} disabled={!position || commentPending} />
                </label>
                <button className={`primary ${commentPending ? "is-busy" : ""}`} type="submit" disabled={!canComment || !position || !comment.trim() || commentPending} aria-busy={commentPending}>
                  <BusyContent busy={commentPending} busyLabel="Posting argument" icon={<MessageSquare size={18} />}>Post argument</BusyContent>
                </button>
              </form>
              {!canComment && <Status tone="warning" icon={<LockKeyhole />} title="Sign in required" body="Arguments are tied to a session and public display label." />}
              {!canComment && (
                <button className="primary" onClick={() => startJoin(undefined, currentAppPath())}>
                  <ShieldCheck size={18} /> Sign in to argue
                </button>
              )}
              {discussionStatus && <Status tone="info" icon={<MessageSquare />} title="Argument status" body={discussionStatus} />}
              <div className="comments" aria-busy={commentsLoading || commentPending}>
                {commentsLoading && <LoadingBlock title="Loading arguments" body="Fetching the latest reviewed points for this issue." />}
                {!commentsLoading && comments.length === 0 && <Status tone="info" icon={<MessageSquare />} title="No arguments yet" body="The first reviewed argument for this issue will appear here." />}
                {comments.map((item) => (
                  <article key={item.id} className="comment discussion-card">
                    <div className="comment-head">
                      <strong>{stanceLabels[item.position]}</strong>
                      <small>{item.status}</small>
                    </div>
                    <p>{item.body}</p>
                    <small>{item.userLabel} / {item.upvotes ?? 0} upvotes</small>
                    <button className={`secondary ${upvotePendingId === item.id ? "is-busy" : ""}`} onClick={() => upvoteComment(item.id)} disabled={!canComment || Boolean(upvotePendingId)} aria-busy={upvotePendingId === item.id}>
                      <BusyContent busy={upvotePendingId === item.id} busyLabel="Updating" icon={<BadgeCheck size={16} />}>Upvote</BusyContent>
                    </button>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>

        <aside className="workspace-rail sticky-panel">
          <section className={`rail-card ballot-card ${votePending ? "is-processing" : ""}`} aria-busy={userVoteLoading || Boolean(votePending)}>
            <div className="rail-card-head">
              <div>
                <p className="eyebrow">Ballot</p>
                <h2>{issue.poll.question}</h2>
              </div>
              <Vote size={18} />
            </div>
            {userVoteLoading ? (
              <LoadingBlock title="Checking ballot" body="Looking for your recorded vote on this issue." />
            ) : userVote ? (
              <div className={`casted-vote stance-${userVote.optionId}`} role="status">
                <span className="casted-vote-head"><CheckCircle2 size={17} /> Vote cast</span>
                <strong>{castedVoteLabel}</strong>
                <small>{castedVoteTime ? `Recorded ${castedVoteTime}` : "Recorded for this vote"}</small>
              </div>
            ) : (
              <div className="vote-options vertical" aria-busy={Boolean(votePending)}>
                {issue.poll.options.map((option) => (
                  <button key={option.id} className={`vote-button stance-${option.id} ${votePending === option.id ? "is-busy" : ""}`} onClick={() => vote(option.id)} title={`Vote ${option.label}`} disabled={!canParticipate || Boolean(votePending)} aria-busy={votePending === option.id}>
                    <BusyContent busy={votePending === option.id} busyLabel="Submitting vote" icon={<Vote size={18} />}>{option.label}</BusyContent>
                  </button>
                ))}
              </div>
            )}
            {!userVote && !userVoteLoading && !canParticipate && <Status tone="warning" icon={<LockKeyhole />} title="Voting locked" body="Sign in, choose your constituency, and use an 18+ participant account before voting." />}
            {!userVote && !userVoteLoading && needsOnboarding && (
              <button className="primary full" onClick={() => startJoin(undefined, currentAppPath())}>
                <BadgeCheck size={18} /> Join to vote
              </button>
            )}
            {voteStatus && <Status tone="info" icon={<Radio />} title="Vote status" body={voteStatus} />}
          </section>

          <section className="rail-card">
            <div className="rail-card-head">
              <h2>Shareable signal</h2>
              <span className="mini-chip">Live</span>
            </div>
            <div className="preview-sheet">
              <div className="preview-sheet-brand">
                <span>Roachsabha.org</span>
                <small>{activeSwarmLabel}</small>
              </div>
              <h3>{issue.title}</h3>
              <div className="preview-signal">
                <span>
                  <strong>{boundedSupportSignal}%</strong>
                  <small>Support</small>
                </span>
                <i aria-hidden="true"><em style={{ width: `${boundedSupportSignal}%` }} /></i>
                <small>{activeVotes.toLocaleString()} local {activeVotes === 1 ? "vote" : "votes"} recorded</small>
              </div>
              <div className="preview-breakdown" aria-label="Local result breakdown">
                {signalBreakdown.map((row) => (
                  <span key={row.id} className={`stance-${row.id}`}>
                    <b>{row.label}</b>
                    <i aria-hidden="true"><em style={{ width: `${row.value}%` }} /></i>
                    <strong>{row.value}%</strong>
                  </span>
                ))}
              </div>
              <p>Advisory citizen signal. Not an official result.</p>
            </div>
            <div className="overview-grid">
              <span><strong>{boundedSupportSignal}%</strong> Support</span>
              <span><strong>{(results?.totalVotes ?? 0).toLocaleString()}</strong> Total votes</span>
            </div>
          </section>

          <section className="rail-card">
            <div className="rail-card-head">
              <h2>Signal overview</h2>
              <span className="mini-chip active">Active</span>
            </div>
            <div className="results-panel">
              {results ? <ResultsBars results={results.swarmResult} /> : resultsLoading ? <ResultsBarsSkeleton /> : <p className="panel-note">Results unavailable. Try refreshing in a moment.</p>}
              <p className="fine-print">
                {(results?.totalVotes ?? 0).toLocaleString()} votes nationally, {(results?.swarmVotes ?? 0).toLocaleString()} in this constituency, {results?.constituenciesRepresented ?? 0} constituencies represented. Updated {results ? new Date(results.updatedAt).toLocaleTimeString() : "pending"}.
              </p>
            </div>
          </section>

          <section className="rail-card" aria-busy={breakdownLoading}>
            <div className="rail-card-head">
              <h2>Constituency heat map</h2>
              <LineChart size={18} />
            </div>
            <ConstituencyHeatMap rows={breakdown} />
          </section>

          <section className="rail-card">
            <div className="rail-card-head">
              <h2>Mandate path</h2>
              <Clock3 size={18} />
            </div>
            <ol className="delivery-list">
              <li className="current"><CheckCircle2 size={16} /><span><strong>Public vote open</strong><small>Citizens are adding constituency signals.</small></span></li>
              <li><CircleDot size={16} /><span><strong>Mandate draft</strong><small>Starts after public signal review.</small></span></li>
              <li><Download size={16} /><span><strong>Representative delivery</strong><small>Prepared after human review.</small></span></li>
            </ol>
          </section>

          <button className={`secondary full ${sharePending ? "is-busy" : ""}`} onClick={shareCard} title="Generate share card" disabled={sharePending} aria-busy={sharePending}>
            <BusyContent busy={sharePending} busyLabel="Generating" icon={<Share2 size={18} />}>Generate share card</BusyContent>
          </button>
          {shareUrl && (
            <figure className="share-preview-frame">
              <img className="share-preview" src={shareUrl} alt={`${issue.title} share card preview`} />
              <figcaption className="share-preview-actions">
                <a className="card-action" href={absoluteAppUrl(shareUrl)} target="_blank" rel="noreferrer">
                  <ExternalLink size={15} /> Open
                </a>
                <button className="secondary" onClick={copyShareCardLink}>
                  <Share2 size={15} /> Copy link
                </button>
              </figcaption>
            </figure>
          )}
          {discuss && (
            <section className="rail-card">
            <div className="panel-head">
              <div>
                <p className="eyebrow">Argument helper</p>
                <h2>AI summary</h2>
              </div>
              <Bot size={18} />
            </div>
                <p className="fine-print">{issue.aiSummary.label}</p>
            {position ? <SummaryBlock issue={issue} position={position} /> : <p className="panel-note">Select a stance to see the matching argument summary.</p>}
            </section>
          )}
        </aside>
      </div>
    </main>
  );
}

function IssueWorkflow() {
  const steps = [
    { title: "Proposed", body: "Issue submitted by citizens", icon: <FileText size={18} /> },
    { title: "Public Vote", body: "Constituency signals build", icon: <Vote size={18} />, active: true },
    { title: "Mandate Draft", body: "Arguments and counts reviewed", icon: <CheckCircle2 size={18} /> },
    { title: "Delivery", body: "Published and sent", icon: <Landmark size={18} /> }
  ];
  return (
    <section className="workflow-strip" aria-label="Mandate lifecycle">
      {steps.map((step, index) => (
        <div key={step.title} className={step.active ? "current" : ""}>
          <span className="workflow-icon">{step.icon}</span>
          <span>
            <strong>{step.title}</strong>
            <small>{step.body}</small>
          </span>
          {index < steps.length - 1 && <ArrowRight size={18} className="workflow-arrow" />}
        </div>
      ))}
    </section>
  );
}

function IssuePoster({ issue }: { issue: Issue }) {
  return (
    <article className="issue-poster" aria-label={`${issue.title} campaign poster`}>
      <div>
        <span>{issue.category}</span>
        <h2>{issue.title}</h2>
        <p>{issue.poll.question}</p>
      </div>
      <div className="poster-art" aria-hidden="true">
        <img src="/roach-mark.svg" alt="" />
        <b>VOTE</b>
      </div>
    </article>
  );
}

function ConstituencyHeatMap({ rows }: { rows: HeatMapRow[] | null }) {
  if (!rows) return <LoadingBlock title="Loading constituency breakdown" body="Aggregated constituency signals are being fetched." />;
  const activeRows = rows.filter((row) => Number(row.result.swarmVotes ?? 0) > 0).sort((a, b) => Number(b.result.swarmVotes ?? 0) - Number(a.result.swarmVotes ?? 0));
  if (activeRows.length === 0) {
    return (
      <div className="heat-map-empty">
        <p className="panel-note">Awaiting constituency-level votes. The map will light up as citizens vote.</p>
        <div className="heat-grid" aria-hidden="true">
          {rows.slice(0, 24).map((row) => <span key={row.swarm.slug} />)}
        </div>
      </div>
    );
  }
  return (
    <div className="heat-map">
      <div className="heat-grid" aria-label="Constituency heat map">
        {activeRows.slice(0, 30).map((row) => {
          const support = row.result.swarmResult.support ?? 0;
          const reform = row.result.swarmResult.needs_reform ?? 0;
          const oppose = row.result.swarmResult.oppose ?? 0;
          const color = support >= reform && support >= oppose ? "support" : reform >= oppose ? "reform" : "oppose";
          return <span key={row.swarm.slug} className={color} title={`${row.swarm.name}: ${support}% support, ${Number(row.result.swarmVotes ?? 0)} votes`} />;
        })}
      </div>
      <div className="heat-list">
        {activeRows.slice(0, 5).map((row) => (
          <span key={row.swarm.slug}>
            <b>{row.swarm.name}</b>
            <small>{Number(row.result.swarmVotes ?? 0)} votes / {row.result.swarmResult.support}% support</small>
          </span>
        ))}
      </div>
      <p className="fine-print">No caste, religion, or sensitive demographics are collected. Public heat maps use aggregated constituency votes only.</p>
    </div>
  );
}

function Community({ issues, bootstrap }: { issues: Issue[]; bootstrap: ApiState | null }) {
  const signedIn = Boolean(bootstrap?.user);
  const [discussions, setDiscussions] = useState<CommunityDiscussionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const categories = [
    { title: "Announcements", body: "Platform updates, transparency notes, and milestones people can share.", icon: <Radio size={22} />, action: "View pulse", route: "/pulse" },
    { title: "Roachsabha Votes", body: "Debate active issue votes and improve the mandate before it is reviewed.", icon: <FileText size={22} />, action: "Explore votes", route: "/sabha" },
    { title: "Drafting Rooms", body: "Join issue-specific argument threads where strong points become mandate material.", icon: <UsersRound size={22} />, action: "Open arguments", route: issues[0] ? `/issues/${issues[0].slug}/discuss` : "/sabha" },
    { title: "Independent Topics", body: "Propose civic topics outside the bill list. We review them before publishing.", icon: <MessageSquare size={22} />, action: "Start topic", route: "/community" },
    { title: "Community Polls", body: "Turn recurring arguments into clean multiple-choice questions people can forward.", icon: <Vote size={22} />, action: "Start poll", route: "/polls" },
    { title: "Fact Check Desk", body: "Attach receipts, links, and context so popular claims stay usable.", icon: <ShieldCheck size={22} />, action: "Review rules", route: "/community" },
    { title: "Volunteer HQ", body: "Join your constituency before contributing as a participant.", icon: <BadgeCheck size={22} />, action: "Join", route: buildJoinPath({ returnTo: "/community" }) },
    { title: "Platform Feedback", body: "Use public discussion to improve the product, process, and safeguards.", icon: <MessageSquare size={22} />, action: "Send feedback", route: issues[0] ? `/issues/${issues[0].slug}/discuss` : "/sabha" }
  ];
  const trending = issues.slice(0, 5);
  const topDiscussions = discussions.slice(0, 5);
  const scrollToDiscussions = () => document.getElementById("community-discussions")?.scrollIntoView({ block: "start", behavior: "smooth" });
  const scrollToProposal = () => document.getElementById("discussion-proposal-form")?.scrollIntoView({ block: "start", behavior: "smooth" });

  async function refreshDiscussions() {
    setLoading(true);
    try {
      const payload = await api<{ discussions: CommunityDiscussionRecord[] }>("/api/community/discussions");
      setDiscussions(payload.discussions);
      setStatus(null);
    } catch (err) {
      setStatus((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshDiscussions().catch(() => undefined);
  }, []);

  return (
    <main className="community-page">
      <section className="community-hero">
        <div>
          <p className="eyebrow">Community Hub</p>
          <h1>Turn hot takes into useful public pressure.</h1>
          <p className="lede small">Start a moderated topic, attach a simple poll, and help the strongest arguments move from group chats into public mandates.</p>
          <div className="hero-actions">
            <button className="primary" onClick={scrollToProposal}><MessageSquare size={18} /> Start a topic</button>
            <button className="secondary" onClick={scrollToDiscussions}><Search size={18} /> Browse arguments</button>
          </div>
          <p className="home-advisory"><Scale size={17} /> Constructive, respectful, evidence-aware dialogue only.</p>
        </div>
        <div className="voice-flow-card">
          <p className="eyebrow">From opinion to mandate</p>
          {[
            ["Vote or propose", "Start with one clear issue people already care about."],
            ["Add arguments", "Make the point short, sourced, and easy to reuse."],
            ["Attach a poll", "Turn debate into a public signal when the question is ready."],
            ["Review and deliver", "Strong citizen-shaped mandates move to human review."]
          ].map(([title, body]) => (
            <div key={title}>
              <span><CheckCircle2 size={18} /></span>
              <p><strong>{title}</strong><small>{body}</small></p>
            </div>
          ))}
        </div>
      </section>

      <section className="page community-grid" id="community-discussions">
        <div className="community-main">
          <CommunityDiscussionProposalForm signedIn={signedIn} onCreated={refreshDiscussions} />
          <p className="eyebrow">Ways to build pressure</p>
          <div className="category-grid">
            {categories.map((category) => (
              <article key={category.title} className="category-card" role="link" tabIndex={0} onClick={() => navigate(category.route)} onKeyDown={(event) => openOnKey(event, category.route)} aria-label={category.action}>
                <span className="bill-icon">{category.icon}</span>
                <h3>{category.title}</h3>
                <p>{category.body}</p>
                <span className="text-arrow">{category.action} <ArrowRight size={15} /></span>
              </article>
            ))}
          </div>
          <section className="community-section">
            <div className="workspace-section-head">
              <div>
                <p className="eyebrow">Independent topics</p>
                <h2>Approved debates ready for signal</h2>
              </div>
              <span className="mini-chip active">{bootstrap?.counts.communityDiscussions ?? discussions.length} live</span>
            </div>
            {status && <Status tone="danger" icon={<AlertTriangle />} title="Topics unavailable" body={status} />}
            {loading && <LoadingBlock title="Loading topics" body="Fetching approved independent debates." />}
            {!loading && discussions.length === 0 && <Status tone="info" icon={<MessageSquare />} title="No independent topics yet" body="Start the first topic. Approved proposals will appear here." />}
            <div className="discussion-feed">
              {discussions.map((discussion) => <DiscussionListItem key={discussion.id} discussion={discussion} />)}
            </div>
          </section>
          <div className="community-banner">
            <span className="brand-mark" aria-hidden="true" />
            <p><strong>A sharper argument today can become a stronger mandate tomorrow.</strong><em>Vote, argue, share.</em></p>
          </div>
          <div className="approval-flow">
            <article>
              <MessageSquare size={22} />
              <strong>Independent topics</strong>
              <p>Anyone can propose a topic. Review keeps spam, defamation, and duplicate threads out before publishing.</p>
            </article>
            <article>
              <Vote size={22} />
              <strong>Community polls</strong>
              <p>Poll proposals support multiple choices and can be linked to a topic after moderation review.</p>
            </article>
          </div>
        </div>
        <aside className="community-rail">
          <section className="rail-card">
            <div className="rail-card-head">
              <h2>Trending arguments</h2>
              <span className="mini-chip active">Live</span>
            </div>
            <div className="ranked-list">
              {topDiscussions.map((discussion, index) => (
                <button key={discussion.slug} onClick={() => navigate(`/discussions/${discussion.slug}`)}>
                  <strong>{index + 1}</strong>
                  <span><b>{discussion.title}</b><small>{discussion.commentCount} arguments / {discussion.poll ? "poll attached" : "topic"}</small></span>
                  <ArrowRight size={14} />
                </button>
              ))}
              {topDiscussions.length === 0 && trending.map((issue, index) => (
                <button key={issue.slug} onClick={() => navigate(`/issues/${issue.slug}/discuss`)}>
                  <strong>{index + 1}</strong>
                  <span><b>{issue.title}</b><small>{issue.category} / {issue.status}</small></span>
                  <ArrowRight size={14} />
                </button>
              ))}
            </div>
          </section>
          <section className="rail-card">
            <h2>Community proof</h2>
            <div className="fact-grid">
              <span><strong>{compactNumber(bootstrap?.counts.comments ?? 0)}</strong><small>Arguments</small></span>
              <span><strong>{compactNumber(bootstrap?.counts.users ?? 0)}</strong><small>Users</small></span>
              <span><strong>{compactNumber(issues.length)}</strong><small>Live issues</small></span>
            </div>
          </section>
          <section className="rail-card">
            <h2>Ground rules</h2>
            <div className="guideline-list">
              <span><ShieldCheck size={16} /> Be respectful and inclusive</span>
              <span><CircleDot size={16} /> Stay on topic</span>
              <span><FileText size={16} /> Bring evidence</span>
              <span><LockKeyhole size={16} /> No spam or self-promotion</span>
            </div>
          </section>
        </aside>
      </section>
    </main>
  );
}

function SwarmPage({ slug }: { slug: string }) {
  const [data, setData] = useState<any>(null);
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  useEffect(() => {
    api(`/api/swarms/${slug}`).then(setData).catch(() => undefined);
  }, [slug]);
  if (!data) return <Loading />;
  const issueSignals = data.issueSignals ?? [];
  const participation = Number(data.votesCastAcrossLiveIssues ?? 0);
  const hasRecordedVotes = participation > 0;
  const avgSupport = hasRecordedVotes && issueSignals.length
    ? Math.round(issueSignals.reduce((sum: number, signal: any) => sum + Number(signal.result?.support ?? 0), 0) / issueSignals.length)
    : 0;
  const activeBills = issueSignals.length;
  const signupCount = Number(data.signupCount ?? 0);
  const shareLink = absoluteAppUrl(`/swarms/${slug}`);

  async function shareSwarm(channel: "native" | "whatsapp" | "x" | "copy") {
    const text = `See how ${data.swarm.name} is voting on Roachsabha. Add your signal if this is your seat`;
    try {
      if (channel === "native" && "share" in navigator) {
        await (navigator as Navigator & { share: (payload: ShareData) => Promise<void> }).share({ title: `${data.swarm.name} signal`, text, url: shareLink });
        setShareStatus("Share sheet opened.");
        return;
      }
      if (channel === "whatsapp") {
        window.open(`https://wa.me/?text=${encodeURIComponent(`${text}: ${shareLink}`)}`, "_blank", "noopener,noreferrer");
        setShareStatus("WhatsApp share opened.");
        return;
      }
      if (channel === "x") {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareLink)}`, "_blank", "noopener,noreferrer");
        setShareStatus("X share opened.");
        return;
      }
      await copyToClipboard(shareLink);
      setShareStatus("Link copied.");
    } catch (err) {
      setShareStatus((err as Error).message);
    }
  }

  return (
    <main className="page swarm-page">
      <div className="breadcrumb">
        <button onClick={() => navigate("/")}>Home</button>
        <ChevronRight size={14} />
        <button onClick={() => navigate("/swarms")}>Signals</button>
        <ChevronRight size={14} />
        <span>{data.swarm.name}</span>
      </div>

      <section className="swarm-hero">
        <div>
          <p className="eyebrow">Constituency Signal</p>
          <h1>{data.swarm.name} signal</h1>
          <p className="lede small">{data.swarm.state} / Lok Sabha Constituency</p>
          <p>See how {data.swarm.name} is voting, what issues are gaining traction, and which signal is worth sharing next.</p>
        </div>
      </section>

      <section className="swarm-metrics">
        <article>
          <span className="bill-icon"><BadgeCheck size={22} /></span>
          <small>Signed up</small>
          <strong>{compactNumber(signupCount)}</strong>
          <em>members in this seat</em>
        </article>
        <article>
          <span className="bill-icon"><UsersRound size={22} /></span>
          <small>Votes cast</small>
          <strong>{compactNumber(participation)}</strong>
          <em>active citizens</em>
        </article>
        <article>
          <span className="bill-icon"><FileText size={22} /></span>
          <small>Live votes</small>
          <strong>{activeBills}</strong>
          <em>being tracked</em>
        </article>
        <article>
          <span className="bill-icon"><LineChart size={22} /></span>
          <small>Support signal</small>
          <strong>{hasRecordedVotes ? `${avgSupport}%` : "No votes"}</strong>
          <em>{hasRecordedVotes ? "average support" : "awaiting signals"}</em>
        </article>
      </section>

      <section className="swarm-layout">
        <div className="swarm-main">
          <section className="rail-card">
            <div className="rail-card-head">
              <h2>Issue-by-issue signal</h2>
              <button className="secondary" onClick={() => navigate("/sabha")}>Vote on issues <ArrowRight size={15} /></button>
            </div>
            <div className="signal-table">
              {issueSignals.map((signal: any) => {
                const support = Number(signal.result?.support ?? 0);
                return (
                  <button key={signal.issue.slug} onClick={() => navigate(`/issues/${signal.issue.slug}`)}>
                    <span className="bill-icon"><FileText size={18} /></span>
                    <span><strong>{signal.issue.title}</strong><small>{signal.issue.riskLevel}</small></span>
                    <b>{hasRecordedVotes ? `${support}%` : "No data"}</b>
                    <span className="support-meter"><i style={{ width: `${hasRecordedVotes ? support : 0}%` }} />{hasRecordedVotes ? (support >= 70 ? "Strongly support" : "Mixed") : "Awaiting votes"}</span>
                    <ArrowRight size={16} />
                  </button>
                );
              })}
            </div>
          </section>

          <section className="swarm-cards">
            <article className="rail-card">
              <h2>Share this signal</h2>
              <p>Send this page to people from {data.swarm.name}. More local votes make the signal harder to ignore.</p>
              <div className="share-actions">
                <button className="secondary" onClick={() => shareSwarm("whatsapp")}>WhatsApp</button>
                <button className="secondary" onClick={() => shareSwarm("x")}>X</button>
                <button className="secondary" onClick={() => shareSwarm("native")}>Share</button>
                <button className="secondary" onClick={() => shareSwarm("copy")}>Copy Link</button>
              </div>
              {shareStatus && <p className="panel-note success" role="status">{shareStatus}</p>}
            </article>
            <article className="rail-card">
              <h2>Participation coverage</h2>
              <div className="fact-grid">
                <span><strong>{compactNumber(participation)}</strong><small>Recorded votes</small></span>
                <span><strong>{activeBills}</strong><small>Tracked votes</small></span>
                <span><strong>{data.representative.status}</strong><small>MP record</small></span>
              </div>
            </article>
          </section>

          <section className="rail-card">
            <div className="rail-card-head">
              <h2>Local arguments</h2>
              <button className="secondary" onClick={() => navigate("/community")}>View community <ArrowRight size={15} /></button>
            </div>
            <div className="discussion-list">
              {issueSignals.slice(0, 3).map((signal: any, index: number) => (
                <button key={signal.issue.slug} onClick={() => navigate(`/issues/${signal.issue.slug}/discuss`)}>
                  <MessageSquare size={18} />
                  <span><strong>Why {data.swarm.name} is debating {signal.issue.title}</strong><small>Citizen thread / {84 + index * 22} arguments</small></span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <aside className="swarm-rail">
          <section className="rail-card">
            <div className="rail-card-head">
              <h2>MP response tracker</h2>
              <Gavel size={18} />
            </div>
            <RepresentativeMini rep={data.representative} />
            <div className="mp-status">
              <strong>Response requested</strong>
              <p>{data.responseTracker}</p>
            </div>
            <button className="primary full" onClick={() => navigate(`/representatives/${data.representative.id}`)}>View MP response history</button>
          </section>
          <section className="rail-card">
            <h2>Public mandate preview</h2>
            <p>This preview turns {compactNumber(participation)} local votes in {data.swarm.name} into a signal people can share.</p>
            <div className="mandate-mini-list">
              {issueSignals.slice(0, 4).map((signal: any) => {
                const support = Number(signal.result?.support ?? 0);
                return <span key={signal.issue.slug}><b>{signal.issue.title}</b><i><em style={{ width: `${hasRecordedVotes ? support : 0}%` }} /></i><strong>{hasRecordedVotes ? `${support}%` : "No data"}</strong></span>;
              })}
            </div>
          </section>
          <section className="rail-card">
            <h2>Constituency leaderboard</h2>
            <Status tone="info" icon={<LineChart />} title="Ranking data pending" body="A public leaderboard will appear after enough constituencies record comparable votes." />
          </section>
          <section className="rail-card">
            <div className="rail-card-head">
              <h2>Members signed up</h2>
              <UsersRound size={18} />
            </div>
            <div className="member-list">
              {(data.recentMembers ?? []).length === 0 && <p className="panel-note">No public members yet. Anonymous members appear without names.</p>}
              {(data.recentMembers ?? []).map((member: any, index: number) => (
                <span key={`${member.label}-${member.joinedAt}-${index}`}>
                  <b>{member.label}</b>
                  <small>{member.role} / joined {new Date(member.joinedAt).toLocaleDateString()}</small>
                </span>
              ))}
            </div>
          </section>
        </aside>
      </section>
    </main>
  );
}

function RepresentativePage({ id }: { id: string }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    api(`/api/representatives/${id}`).then(setData).catch(() => undefined);
  }, [id]);
  if (!data) return <Loading />;
  return (
    <main className="page narrow">
      <SectionTitle icon={<Gavel />} title="MP Response Tracker" action={data.responseStatus} body="A clean view of representative responses beside constituency-level issue signals." />
      <RepresentativeMini rep={data.representative} />
      <div className="neutral-list">
        {(data.neutralLanguage ?? [
          "Citizen signals are advisory and non-binding.",
          "Verified representative responses are published in neutral language.",
          "Constituency results show participation counts and issue-specific percentages."
        ]).map((line: string) => (
          <span key={line}>{line}</span>
        ))}
      </div>
      <div className="issue-list">
        {data.issueSignals.map((signal: any) => (
          <article key={signal.issue.slug} className="issue-row">
            <div>
              <h3>{signal.issue.title}</h3>
              <ResultsBars results={signal.result.swarmResult} compact />
              <small>{signal.responseStatus.replaceAll("_", " ")}</small>
            </div>
            <button className="secondary" onClick={() => navigate(`/issues/${signal.issue.slug}`)}>
              <ArrowRight size={16} /> Open
            </button>
          </article>
        ))}
      </div>
      <Status tone="info" icon={<Scale />} title="Language policy" body="This page stays neutral unless delivery, response windows, and counsel-reviewed evidence support stronger wording." />
    </main>
  );
}

function MandatePage({ slug }: { slug: string }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    api(`/api/mandates/${slug}`).then(setData).catch(() => undefined);
  }, [slug]);
  if (!data) return <Loading />;
  return (
    <main className="page">
      <SectionTitle icon={<FileText />} title={data.mandate.title} action={data.mandate.status} body="The public signal translated into a reviewed, non-binding citizen draft." />
      <div className="two-col wide">
        <section>
          <p className="lede small">Support: {data.mandate.supportPercent}% from {data.mandate.voteCount.toLocaleString()} recorded mandate votes.</p>
          <div className="clause-list">
            {data.mandate.clauses.map((clause: any) => (
              <article key={clause.number} className="clause">
                <strong>Clause {clause.number}: {clause.title}</strong>
                <p>{clause.body}</p>
                <small>Risk: {clause.riskLabel}</small>
              </article>
            ))}
          </div>
        </section>
        <section className="tool-panel">
          <h2>Mandate lifecycle</h2>
          <ol className="workflow">
            {data.workflow.map((step: string) => <li key={step}>{step.replaceAll("_", " ")}</li>)}
          </ol>
          <Status tone="warning" icon={<Scale />} title="Human review" body="Legal and editorial review is required before publication or representative delivery." />
        </section>
      </div>
    </main>
  );
}

function IssueCard({ issue }: { issue: Issue }) {
  const path = `/issues/${issue.slug}`;
  return (
    <article className="issue-card clickable-card" role="link" tabIndex={0} aria-label={`Open ${issue.title}`} onClick={() => navigate(path)} onKeyDown={(event) => openOnKey(event, path)}>
      <div className="meta-row">
        <span>{issue.category}</span>
        <span>{issue.riskLevel}</span>
      </div>
      <h3>{issue.title}</h3>
      <p>{issue.brief}</p>
      <small>{issue.poll.question}</small>
      <span className="card-action"><ArrowRight size={16} /> Open vote</span>
    </article>
  );
}

function IssueRow({ issue }: { issue: Issue }) {
  const path = `/issues/${issue.slug}`;
  const [opening, setOpening] = useState(false);
  const openIssue = () => {
    if (opening) return;
    setOpening(true);
    navigateAfterPaint(path);
  };
  const openIssueOnKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openIssue();
  };

  return (
    <article className={`issue-row clickable-row ${opening ? "is-opening" : ""}`} role="link" tabIndex={0} aria-label={`Vote on ${issue.title}`} aria-busy={opening} onClick={openIssue} onKeyDown={openIssueOnKey}>
      <div>
        <div className="meta-row inline">
          <span>{issue.category}</span>
          <span>{issue.riskLevel}</span>
        </div>
        <h3>{issue.title}</h3>
        <p>{issue.poll.question}</p>
      </div>
      <span className="row-action">
        {opening ? <InlineLoader label="Opening" /> : <><Vote size={16} /> Vote</>}
      </span>
    </article>
  );
}

function ResultsBars({ results, compact = false }: { results: Record<string, number>; compact?: boolean }) {
  return (
    <div className={compact ? "bars compact" : "bars"} aria-label="Result percentages">
      {Object.entries(stanceLabels).map(([key, label]) => {
        const value = Math.max(0, Math.min(100, results[key] ?? 0));
        return (
          <div key={key} className="bar-row">
            <span>{label}</span>
            <div className="bar-track"><span style={{ width: `${value}%` }} /></div>
            <strong>{value}%</strong>
          </div>
        );
      })}
    </div>
  );
}

function ResultsBarsSkeleton({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? "bars compact skeleton-bars" : "bars skeleton-bars"} role="status" aria-live="polite" aria-label="Loading result percentages">
      {Object.values(stanceLabels).map((label) => (
        <div key={label} className="bar-row">
          <span>{label}</span>
          <div className="bar-track"><span /></div>
          <strong>--</strong>
        </div>
      ))}
    </div>
  );
}

function SummaryBlock({ issue, position }: { issue: Issue; position: StanceKey }) {
  const summary = issue.aiSummary.positions[position];
  return (
    <div className="summary">
      <strong>Top argument</strong>
      <p>{summary.topArgument}</p>
      <strong>Common concern</strong>
      <p>{summary.commonConcern}</p>
      {summary.evidenceCited && (
        <>
          <strong>Evidence base</strong>
          <p>{summary.evidenceCited}</p>
        </>
      )}
      <strong>Counterpoint</strong>
      <p>{summary.counterpoint}</p>
      {summary.reviewFlags.length > 0 && <Status tone="warning" icon={<Flag />} title="Review flag" body={summary.reviewFlags.join(" ")} />}
    </div>
  );
}

function RepresentativeMini({ rep }: { rep: Representative }) {
  return (
    <div className="rep-mini">
      <RepresentativePhoto rep={rep} />
      <div>
        <h3>{rep.name}</h3>
        <p>{rep.party} ({rep.partyShort})</p>
        <small>{rep.status}</small>
      </div>
    </div>
  );
}

function RepresentativePhoto({ rep, size = "regular" }: { rep?: Representative; size?: "small" | "regular" }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [rep?.photoUrl]);

  const className = `rep-photo ${size}`;
  if (!rep?.photoUrl || failed) {
    return <div className={`avatar ${size}`}>{representativeInitials(rep)}</div>;
  }

  return <img className={className} src={rep.photoUrl} alt={`${rep.name} photo`} loading="lazy" decoding="async" onError={() => setFailed(true)} />;
}

function representativeInitials(rep?: Representative) {
  const words = (rep?.name ?? "Roachsabha")
    .replace(/\b(Shri|Smt|Dr|Prof|No|sitting|representative|recorded)\b/gi, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return (words.slice(0, 2).map((word) => word[0]).join("") || "RS").toUpperCase();
}

function SectionTitle({ icon, title, action, body }: { icon: ReactNode; title: string; action: string; body?: string }) {
  return (
    <div className="section-title">
      <div>
        <span className="section-icon">{icon}</span>
        <span>
          <h1>{title}</h1>
          {body && <p>{body}</p>}
        </span>
      </div>
      <span>{action}</span>
    </div>
  );
}

function Status({ tone, icon, title, body }: { tone: "info" | "success" | "warning" | "danger"; icon: ReactNode; title: string; body: string }) {
  return (
    <div className={`status ${tone}`} role="status">
      {icon}
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
    </div>
  );
}

function LoadingBlock({ title, body }: { title: string; body: string }) {
  return (
    <div className="loading-block" role="status" aria-live="polite">
      <InlineLoader label={title} />
      <p>{body}</p>
    </div>
  );
}

function Loading({ title = "Loading", body = "Fetching current civic data." }: { title?: string; body?: string }) {
  return (
    <main className="page narrow">
      <LoadingBlock title={title} body={body} />
    </main>
  );
}

function NotFound() {
  return (
    <main className="page narrow">
      <Status tone="danger" icon={<AlertTriangle />} title="Page not found" body="The page may have moved, or the link may be incomplete." />
      <button className="primary" onClick={() => navigate("/")}>Return home</button>
    </main>
  );
}
